export type Language = 'en' | 'si' | 'ta';

export const translations = {
  en: {
    // App
    appName: 'Vimana Travels',
    tagline: 'Your Trusted Visa Partner',

    // Navigation
    home: 'Home',
    apply: 'Apply Now',
    status: 'Check Status',
    faq: 'Help & FAQ',
    contact: 'Contact Us',

    // Home Screen
    welcomeTitle: 'Get Your India Visa',
    welcomeSubtitle: 'Simple. Fast. Trusted.',
    heroDescription: '1 Year Multiple Entry Tourist Visa for India',
    processingTime: 'Ready in 72 Hours',
    price: 'Rs. 14,900',
    price30Days: 'Rs. 9,900',
    price1Year: 'Rs. 14,900',
    priceEBusiness1Year: 'Rs. 15,000',
    startApplication: 'Start Your Application',
    getVisaNow: 'Get Your Visa Now',
    needHelpWhatsApp: 'Need help? WhatsApp us',
    checkExistingStatus: 'Check Existing Application',
    continueApplication: 'Continue Application',
    continueApplicationDesc: 'Enter your application number to continue where you left off.',
    continueApplicationPlaceholder: 'e.g. VIM-2026-171522',
    continueApplicationSubmit: 'Continue',
    continueApplicationEnterNumber: 'Enter your application number',
    continueApplicationNotFound: 'Application not found. Check the number and try again.',
    continueApplicationAlreadySubmitted: 'This application is already submitted. Use Check Status to view it.',
    cancel: 'Cancel',

    // Features
    feature1Title: 'Easy Process',
    feature1Desc: 'Just 7 simple steps to get your visa',
    feature2Title: 'Fast Service',
    feature2Desc: 'We apply for your visa within 24 hours',
    feature3Title: 'Expert Support',
    feature3Desc: 'Our team helps you every step',

    // Application Flow
    step: 'Step',
    of: 'of',
    selectCountry: 'Select Country',
    selectCountryDesc: 'Choose where you want to travel',
    uploadPassport: 'Upload Passport',
    uploadPassportDesc: 'Take a photo of your passport',
    payment: 'Payment',
    paymentStepDesc: 'Complete your payment',
    done: 'Done!',
    doneDesc: 'Application submitted',

    // Country Selection
    whereToTravel: 'Where do you want to travel?',
    india: 'India',
    indiaVisa: 'Indian Tourist Visa',
    multipleEntry: '1 Year Multiple Entry',
    selectIndia: 'Select India',
    comingSoon: 'Coming Soon',
    moreCountriesSoon: 'More countries will be added soon',
    continue: 'Continue',
    back: 'Go Back',

    // Passport Upload
    passportPhotoTitle: 'Take a Photo of Your Passport',
    passportPhotoDesc: 'We need the page with your photo and details',
    tapToTakePhoto: 'Tap here to take a photo',
    orUploadFile: 'Or select from your files',
    photoTips: 'Photo Tips',
    tip1: 'Place passport on a flat surface',
    tip2: 'Make sure there is good lighting',
    tip3: 'Include all four corners of the page',
    tip4: 'Avoid shadows and glare',
    retakePhoto: 'Take Another Photo',
    useThisPhoto: 'Use This Photo',
    scanPassport: 'Scan Your Passport',
    autoAlignDesc: 'Auto-aligns and crops your document',
    opensCameraApp: 'Opens your camera',
    photoCaptured: 'Photo captured',
    passportExample: 'Position like this:',
    placePassport: 'Place your passport inside the frame',
    moveCloser: 'Move closer',
    moreLightNeeded: 'More light needed',
    holdSteady: 'Hold steady',
    capture: 'Capture',

    // Payment
    paymentTitle: 'Complete Your Payment',
    paymentDesc: 'Pay securely to submit your application',
    totalAmount: 'Total Amount',
    paymentMethods: 'Payment Methods',
    bankTransfer: 'Bank Transfer',
    cashPayment: 'Cash Payment',
    paymentCard: 'Pay by Card',
    payWithCard: 'Pay with Card',
    payLater: 'Pay at our office or via bank transfer',
    submitApplication: 'Submit Application',
    cardSecure: 'Secure payment powered by Stripe',
    cardPaymentError: 'Card payment failed. Please try again.',
    bankPaymentError: 'Failed to update payment method. Please try again.',
    paymentConfirming: 'Confirming your payment…',
    paymentSuccessToast: 'Payment successful! Your application has been submitted.',
    paymentSuccessPopupTitle: 'Payment succeeded',
    paymentSuccessPopupDesc: 'Your payment was completed successfully.',
    refundPolicyTitle: 'Refund Policy',
    refundPolicyIntro: 'At Vimana Travels, we are committed to transparency in our visa services. Please read our refund policy carefully before making a payment.',
    refundPolicyVisaRejection: 'Visa Rejection Refunds',
    refundPolicyVisaRejectionDesc: 'If a customer\'s visa application is rejected by the relevant embassy, refunds will be handled as follows:',
    refundPolicyServiceFeeRefundable: 'Our service fee is refundable',
    refundPolicyEmbassyFeeNonRefundable: 'Embassy / visa fees are non-refundable, as these are paid directly to the relevant authority and are outside our control',
    refundPolicyBreakdown: 'Refund Breakdown by Visa Type',
    refundPolicy1YearTitle: '1 Year Multiple Entry Tourist Visa',
    refundPolicy1YearTotal: 'Total Fee: LKR 14,900/-',
    refundPolicy1YearRefundable: 'Refundable (Service Fee): LKR 5,400/-',
    refundPolicy1YearNonRefundable: 'Non-Refundable (Visa Fee): LKR 9,500/-',
    refundPolicy1MonthTitle: '1 Month Single Entry Tourist Visa',
    refundPolicy1MonthTotal: 'Total Fee: LKR 9,900/-',
    refundPolicy1MonthRefundable: 'Refundable (Service Fee): LKR 4,400/-',
    refundPolicy1MonthNonRefundable: 'Non-Refundable (Visa Fee): LKR 5,500/-',
    refundPolicyImportantNotes: 'Important Notes',
    refundPolicyNote1: 'Refunds apply only in case of visa rejection',
    refundPolicyNote2: 'No refunds will be issued once a visa is approved or issued',
    refundPolicyClarification: 'For any clarification, please contact our support team before submitting your application.',
    refundPolicyNote3: 'Refunds will be processed within a reasonable timeframe after receiving official rejection confirmation',

    // Success
    successTitle: 'Application Submitted!',
    successDesc: 'We have received your visa application',
    applicationNumber: 'Your Application Number',
    saveThisNumber: 'Please save this number to check your status',
    whatNext: 'What happens next?',
    nextStep1: 'We will process your application',
    nextStep2: 'You will receive updates via WhatsApp',
    nextStep3: 'Your visa will be ready in 72 hours',
    goHome: 'Return to Home',

    // Status Check
    statusTitle: 'Check Your Application',
    statusDesc: 'Enter your application number to see the status',
    enterPassportNumber: 'Enter Passport Number',
    passportNumberPlaceholder: 'e.g., N1234567',
    orUseAppNumber: 'Or use Application Number',
    enterAppNumber: 'Enter Application Number',
    appNumberPlaceholder: 'e.g., VIM-2024-001234',
    checkStatus: 'Check Status',

    // Contact Info Collection
    contactInfoTitle: 'How can we contact you?',
    contactInfoDesc: 'We will send you updates about your visa',
    emailLabel: 'Email Address',
    emailPlaceholder: 'your@email.com',
    phoneLabel: 'Phone Number',
    phonePlaceholder: '07X XXX XXXX',
    atLeastOne: 'Please enter at least one contact method',
    statusPending: 'Processing',
    statusApproved: 'Approved',
    statusReady: 'Ready for Collection',
    pendingDesc: 'Your application is being processed',
    approvedDesc: 'Your visa has been approved!',
    readyDesc: 'Your visa is ready. Please collect it.',

    // FAQ
    faqTitle: 'Frequently Asked Questions',
    faqSubtitle: 'Find answers to common questions',
    faq1Q: 'What is a 1-year multiple entry tourist visa?',
    faq1A: 'A 1-year multiple entry tourist visa allows you to travel to India multiple times within one year from the date of issue. You can enter and exit India as many times as you wish during the validity period, as long as each stay follows Indian immigration rules.',

    faq2Q: 'What is a 1-month single entry tourist visa?',
    faq2A: 'A 1-month single entry tourist visa allows you to enter India only once and stay for the permitted duration. Once you exit India, the visa becomes invalid even if the 1-month period has not ended.',

    faq3Q: 'Do you process business visas?',
    faq3A: 'Yes. We process business visas, subject to eligibility and embassy requirements. Please contact us in advance so we can review your purpose of travel and required documents before proceeding.',

    faq4Q: 'Can I land at any airport or port in India using a multiple entry visa?',
    faq4A: 'Yes. With a valid multiple entry tourist visa, you can enter India through any authorized airport or seaport, subject to immigration approval.',

    faq5Q: 'Can I work in India using a 1-year multiple entry tourist visa?',
    faq5A: 'No. Working in India is strictly not allowed on a tourist visa. Tourist visas are only for travel, sightseeing, visiting friends or relatives, and similar non-business purposes.',

    faq6Q: 'How long does it take to process the visa?',
    faq6A: 'Visa processing usually takes a minimum of 3 working days, but it can take 5 or more working days depending on embassy processing times and additional verification.',

    faq7Q: 'Do you provide sticker visas?',
    faq7A: 'No. We do not provide sticker visas at this time. All visas we process are electronic non-sticker visas.',

    faq8Q: 'Do you help with flight ticket booking?',
    faq8A: 'Yes. We assist with flight ticket booking, including ticket arrangements required for visa purposes.',

    faq9Q: 'Do you process visas for other countries?',
    faq9A: 'No. Currently, we only process visas for India. Other countries may be added in the future.',

    faq10Q: 'How many times can I travel to India with a 1-year multiple entry tourist visa?',
    faq10A: 'You can travel multiple times without a fixed limit during the 1-year validity period, as long as you comply with Indian immigration rules for each stay.',

    faq11Q: 'What documents are required to apply for a tourist visa?',
    faq11A: 'You only need a scanned copy of your passport and a recent passport-size photograph. Additional documents may be requested if required by the embassy.',

    // Contact
    contactTitle: 'Need Help?',
    contactDesc: 'We are here to help you',
    callUs: 'Call Us',
    whatsappUs: 'WhatsApp Us',
    visitOffice: 'Visit Our Office',
    officeHours: 'Monday to Saturday, 9 AM - 6 PM',
    phoneNumber: '076 873 4396',
    phoneNumberRaw: '94768734396',

    // Visa Application Form - Step Titles
    startApplicationTitle: 'Start Application',
    startApplicationDesc: 'Please provide your basic information',
    uploadDocumentsTitle: 'Upload Documents',
    uploadDocumentsDesc: 'Upload your passport and photograph',
    travelDetailsTitle: 'Travel Details',
    travelDetailsDesc: 'Tell us about your travel plans',
    personalFamilyTitle: 'Personal & Family Details',
    personalFamilyDesc: 'Information about you and your family',
    addressEducationTitle: 'Address, Education & Religion',
    addressEducationDesc: 'Additional personal information',
    travelHistoryTitle: 'Travel History',
    travelHistoryDesc: 'Information about your previous travels',
    referenceInIndiaTitle: 'Reference in India',
    referenceInIndiaDesc: 'Contact details of your reference in India',
    referenceNotRequiredDesc: 'Not required for tourism',
    reviewDeclarationTitle: 'Review & Declaration',
    reviewDeclarationDesc: 'Review your application before submitting',
    applicationIdCopyPrompt: 'You can copy this and continue filling the form whenever you have time',

    // Step 1: Start Application
    applicationId: 'Application Number',
    applicationIdWillBeGenerated: 'Your application number will be generated upon submission',
    applicationOpen: 'Application Open',
    fullNameLabel: 'Full Name (as per passport) *',
    fullNamePlaceholder: 'Enter your full name',
    phoneNumberLabel: 'WhatsApp Number *',
    phonenumberhelper: 'Enter your phone number (e.g. 077 123 4567 or 77 123 4567)',
    phoneNumberPlaceholder: '07X XXX XXXX',
    emailAddressLabel: 'Email Address *',
    visaTypeLabel: 'Choose which visa type *',
    visaTypePlaceholder: 'Select visa type',
    visaType30Days: 'eTourist Visa (for 30 Days)',
    visaType1Year: 'eTourist Visa (for 1 Year)',
    visaType5Years: 'eTourist Visa (for 5 Years)',
    visaTypeEBusiness1Year: 'eBusiness Visa (for 1 Year)',
    businessTravelPurposeLabel: 'Which purpose do you want to travel?',
    businessTravelPurposePlaceholder: 'Select purpose',
    businessTravelPurposePopupTitle: 'Documents required',
    ok: 'OK',
    // eBusiness travel purposes (labels)
    purpose_SET_UP_BUSINESS_VENTURE: 'TO SET UP INDUSTRIAL/BUSINESS VENTURE',
    purpose_SALE_PURCHASE_TRADE: 'SALE / PURCHASE / TRADE',
    purpose_ATTEND_BUSINESS_MEETINGS: 'ATTEND TECHNICAL / BUSINESS MEETINGS',
    purpose_TO_RECRUIT_MANPOWER: 'TO RECRUIT MANPOWER',
    purpose_EXHIBITIONS_TRADE_FAIRS: 'PARTICIPATION IN EXHIBITIONS, BUSINESS / TRADE FAIRS',
    purpose_EXPERT_SPECIALIST_PROJECT: 'EXPERT / SPECIALIST IN CONNECTION WITH AN ONGOING PROJECT',
    purpose_CONDUCTING_TOURS: 'CONDUCTING TOURS',
    purpose_GIAN_LECTURES: 'TO DELIVER LECTURE/S UNDER GLOBAL INITIATIVE FOR ACADEMIC NETWORKS (GIAN)',
    purpose_SPORTS_RELATED_ACTIVITY: 'SPORTS RELATED ACTIVITY',
    purpose_TO_JOIN_THE_VESSEL: 'TO JOIN THE VESSEL',
    // eBusiness purpose popup messages
    purposePopup_SET_UP_BUSINESS_VENTURE: 'You will need a copy of business invitation and business cardto apply for this.',
    purposePopup_SALE_PURCHASE_TRADE: 'You will need a copy of business invitation and business card to apply for this.',
    purposePopup_ATTEND_BUSINESS_MEETINGS: 'You will need a copy of business invitation and business cardto apply for this.',
    purposePopup_TO_RECRUIT_MANPOWER: 'You will need a copy of business invitation and business cardto apply for this.',
    purposePopup_EXHIBITIONS_TRADE_FAIRS: 'You will need a copy of business invitation and business cardto apply for this.',
    purposePopup_EXPERT_SPECIALIST_PROJECT: 'You will need a copy of business invitationand business card to apply for this.',
    purposePopup_CONDUCTING_TOURS: 'You will need a copy of business invitation and business card to apply for this.',
    purposePopup_GIAN_LECTURES: 'You will need: Invitation of the host institute to the foreign faculty, Copy of the sanction order under GIAN issued by the National Coordinating Institute viz. IIT Kharagpur, Copy of the synopsis of the courses to be taken up by the faculty to apply for this.',
    purposePopup_SPORTS_RELATED_ACTIVITY: 'You will need: Approval of the Government of India in the Ministry of Youth Affairs and Sports (Department of Sports), Invitation Letter to foreign sports teams and sports persons to visit India by the concerned Sports Federation/Association in India to apply for this.',
    purposePopup_TO_JOIN_THE_VESSEL: 'You will need: Sponsoring Letter from the Indian Shipping Agent/Foreign Shipping Agent stationed in India, Copy of the Continuous Discharge Certificate (CDC) of the Seamen to apply for this.',
    ebusinessDocsTitle: 'Upload supporting documents',
    ebusinessDocsDesc: 'Upload the PDFs required for your selected travel purpose. Each file will be saved with a clear name.',
    uploadPdf: 'Upload PDF',
    docUploaded: 'Uploaded',
    replace: 'Replace',
    ebusinessUploadError: 'Failed to upload document. Please try again.',
    doc_BUSINESS_INVITATION: 'Business invitation (copy)',
    doc_BUSINESS_CARD: 'Business card',
    doc_GIAN_INVITATION: 'Invitation of the host institute to the foreign faculty',
    doc_GIAN_SANCTION_ORDER: 'Copy of sanction order under GIAN (IIT Kharagpur)',
    doc_GIAN_SYNOPSIS: 'Synopsis of the courses to be taken up by the faculty',
    doc_SPORTS_APPROVAL: 'Approval of Govt of India (Ministry of Youth Affairs & Sports)',
    doc_SPORTS_INVITATION_LETTER: 'Invitation letter from Sports Federation/Association in India',
    doc_VESSEL_SPONSORING_LETTER: 'Sponsoring letter from Indian/Foreign Shipping Agent in India',
    doc_VESSEL_CDC: 'Copy of Continuous Discharge Certificate (CDC) of the Seamen',

    // Step 2: Upload Documents
    passportMandatory: 'Passport (mandatory) *',
    photographMandatory: 'Passport-size Photograph (mandatory) *',
    passportUploaded: 'Passport uploaded',
    photographUploaded: 'Photograph uploaded',
    takePhoto: 'Take Photo',
    uploadFile: 'Or Upload File',
    remove: 'Remove',
    uploadPhotograph: 'Upload Photograph',
    clickToSelectFile: 'Click to select a file',
    passportValidityNote: '• Passport must be valid for at least 6 months',
    passportClearNote: '• Clear image, all corners visible',
    photoFrontFacing: '• Front-facing, plain background',
    photoRecentNote: '• Clear, recent photograph',
    photoFormatJpeg: '• Format: JPEG',
    photoSizeRequirements: '• Size: Minimum 10 KB, Maximum 1 MB',
    photoDimensionRequirements: '• The minimum dimensions are 350 pixels (width) × 350 pixels (height).',
    photoWhiteBackgroundNote: '• Recent front facing photograph with white background.',

    // Step 3: Travel Details
    portOfArrivalLabel: 'Port of Arrival *',
    portOfArrivalPlaceholder: 'Select port of arrival',
    expectedArrivalLabel: 'Expected Date of Arrival *',
    placesToVisitLabel: 'Places Intended to Be Visited *',
    placesToVisitPlaceholder: 'e.g., Delhi, Agra, Jaipur, Mumbai',
    placesToVisitHelper: 'List the cities or places you plan to visit',

    // Step 4: Personal & Family Details
    fatherDetails: "Father's Details",
    fatherNameLabel: "Father's Full Name *",
    fatherNamePlaceholder: "Enter father's full name",
    fatherBirthplaceLabel: "Father's Place of Birth *",
    fatherBirthplacePlaceholder: 'City, Country',
    motherDetails: "Mother's Details",
    motherNameLabel: "Mother's Full Name *",
    motherNamePlaceholder: "Enter mother's full name",
    motherBirthplaceLabel: "Mother's Place of Birth *",
    motherBirthplacePlaceholder: 'City, Country',
    maritalStatusLabel: 'Marital Status *',
    maritalStatusPlaceholder: 'Select marital status',
    single: 'Single',
    married: 'Married',
    other: 'Other',
    spouseDetails: 'Spouse Details',
    spouseNameLabel: "Spouse's Full Name *",
    spouseNamePlaceholder: "Enter spouse's full name",
    spouseNationalityLabel: "Spouse's Nationality *",
    spouseNationalityPlaceholder: "Enter spouse's nationality",
    spouseBirthplaceLabel: "Spouse's Place of Birth *",
    spouseBirthplacePlaceholder: 'City, Country',

    // Step 5: Address, Education & Religion
    residentialAddressLabel: "Applicant's Residential Address *",
    residentialAddressPlaceholder: 'Enter your complete residential address',
    educationLabel: 'Educational Qualifications *',
    educationPlaceholder: "e.g., Post Graduate",
    religionLabel: 'Religion *',
    religionPlaceholder: 'Enter your religion',
    educationalQualificationInstituteLabel: 'Qualification acquired from College/University?',
    educationalQualificationInstitutePlaceholder: 'e.g. University of Colombo',

    // Step 6: Travel History
    visitedIndiaLabel: 'Have you visited India before? *',
    citiesVisitedInIndiaLabel: 'Cities visited in India',
    citiesVisitedInIndiaPlaceholder: 'List the cities you visited in India',
    previousVisaIdLabel: 'Mention your previous visa ID',
    previousVisaIdPlaceholder: 'Visa ID number',
    visitedSAARCLabel: 'Have you visited any SAARC countries in last 3 years? *',
    saarcCountriesList: '(Afghanistan, Bangladesh, Bhutan, India, Maldives, Nepal, Pakistan)',
    overstayHistoryLabel: 'Any history of overstaying a visa? *',
    visaRejectionsLabel: 'Any previous visa rejections? *',
    refusedEntryLabel: 'Ever refused entry to any country? *',
    arrestedConvictedLabel: 'Have you ever been arrested / prosecuted / convicted by Court of Law of any country? *',
    traffickingCrimesLabel: 'Have you ever been engaged in Human trafficking / Drug trafficking / Child abuse / Crime against women / Economic offense / Financial fraud? *',
    cyberTerrorViolenceLabel: 'Have you ever been engaged in Cyber crime / Terrorist activities / Sabotage / Espionage / Genocide / Political killing / other act of violence? *',
    glorifyTerrorismLabel: 'Have you ever by any means or medium, expressed views that justify or glorify terrorist violence or that may encourage others to terrorist acts or other serious criminal acts? *',
    soughtAsylumLabel: 'Have you sought asylum (political or otherwise) in any country? *',
    travelExplanationLabel: 'Please provide explanation',
    travelExplanationPlaceholder: 'Provide details about the circumstances',
    overstayExplanationLabel: 'Please provide explanation for overstaying',
    overstayExplanationPlaceholder: 'Provide details about the overstaying circumstances',
    visaRejectionExplanationLabel: 'Please provide explanation for visa rejection',
    visaRejectionExplanationPlaceholder: 'Provide details about the visa rejection',
    refusedEntryExplanationLabel: 'Please provide explanation for refused entry',
    refusedEntryExplanationPlaceholder: 'Provide details about the refused entry',

    // Step 7: Reference in India
    referenceNotRequiredTourism: 'Reference in India not required for tourism.',
    proceedNextStep: 'You can proceed to the next step.',
    referenceNameLabel: 'Name of relative / friend in India *',
    referenceNamePlaceholder: 'Enter reference name',
    referenceAddressLabel: 'Address *',
    referenceAddressPlaceholder: 'Enter complete address in India',
    referencePhoneLabel: 'Phone number *',
    referencePhonePlaceholder: '+91 XXXXXXXXXX',

    // Step 8: Review & Declaration
    applicationSummary: 'Application Summary',
    personalInformation: 'Personal Information',
    documentsUploaded: 'Documents Uploaded',
    travelDetails: 'Travel Details',
    personalFamilyDetails: 'Personal & Family Details',
    additionalInformation: 'Additional Information',
    arrival: 'Arrival',
    port: 'Port',
    father: 'Father',
    mother: 'Mother',
    maritalStatusValue: 'Status',
    educationValue: 'Education',
    religionValue: 'Religion',
    declarationText: 'I hereby declare that the information provided above is true and correct to the best of my knowledge and belief. In case any of the above information is found to be false or untrue or misleading or misrepresenting, I am aware that I may be held liable for it.',
    acceptDeclarationPrompt: 'Please accept the declaration to submit your application.',

    // Navigation & Actions
    stepOf: 'Step {step} of {total}',
    submitButton: 'Submit Application',
    processing: 'Processing...',

    // Common
    loading: 'Loading...',
    error: 'Something went wrong',
    tryAgain: 'Please try again',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    close: 'Close',

    // Language
    language: 'Language',
    english: 'English',
    sinhala: 'සිංහල',
    tamil: 'தமிழ்',
    selectLanguageDesc: 'Please choose your preferred language for the application',
  },
  si: {
    // App
    appName: 'විමාන ට්‍රැවල්ස්',
    tagline: 'ඔබේ විශ්වාසනීය වීසා සහකරු',

    // Navigation
    home: 'මුල් පිටුව',
    apply: 'අයදුම් කරන්න',
    status: 'තත්ත්වය පරීක්ෂා කරන්න',
    faq: 'උදව් සහ නිතර අසන ප්‍රශ්න',
    contact: 'අප හා සම්බන්ධ වන්න',

    // Home Screen
    welcomeTitle: 'ඔබේ ඉන්දියානු වීසා ලබාගන්න',
    welcomeSubtitle: 'සරලයි. වේගවත්. විශ්වාසවන්තයි.',
    heroDescription: 'ඉන්දියාවට වසරක බහු ප්‍රවේශ සංචාරක වීසා',
    processingTime: 'පැය 72 තුළ ලැබේ',
    price: 'රු. 14,900',
    price30Days: 'රු. 9,900',
    price1Year: 'රු. 14,900',
    priceEBusiness1Year: 'රු. 15,000',
    startApplication: 'අයදුම්පත ආරම්භ කරන්න',
    getVisaNow: 'ඔබේ වීසා දැන්ම ලබාගන්න',
    needHelpWhatsApp: 'උදව් අවශ්‍යද? අපට WhatsApp කරන්න',
    checkExistingStatus: 'පවතින අයදුම්පත පරීක්ෂා කරන්න',
    continueApplication: 'අයදුම් කිරීම ඉදිරියට කරගෙන යන්න',
    continueApplicationDesc: 'ඔබ නතර කළ තැනින් ඉදිරියට කරගෙන යාමට ඔබේ අයදුම්පත් අංකය ඇතුළත් කරන්න.',
    continueApplicationPlaceholder: 'උදා: VIM-2026-171522',
    continueApplicationSubmit: 'ඉදිරියට යන්න',
    continueApplicationEnterNumber: 'ඔබේ අයදුම් අංකය ඇතුළත් කරන්න',
    continueApplicationNotFound: 'අයදුම්පත හමු නොවීය. අංකය පරීක්ෂා කර නැවත උත්සාහ කරන්න.',
    continueApplicationAlreadySubmitted: 'මෙම අයදුම්පත දැනටමත් යොමු කර ඇත. එය බැලීමට නියමය බලන්න භාවිතා කරන්න.',

    // Features
    feature1Title: 'පහසු ක්‍රියාවලිය',
    feature1Desc: 'ඔබේ වීසා ලබා ගැනීමට ඇත්තේ සරල පියවර 7ක් පමණයි',
    feature2Title: 'වේගවත් සේවාව',
    feature2Desc: 'අපි පැය 24ක් ඇතුළත ඔබේ වීසා සඳහා අයදුම් කරන්නෙමු',
    feature3Title: 'විශේෂඥ සහාය',
    feature3Desc: 'සෑම පියවරකදීම අපගේ කණ්ඩායම ඔබට සහාය වේ',
    phonenumberhelper: 'ඔබේ දුරකථන අංකය ඇතුළත් කරන්න (උදා: 077 123 4567 හෝ 77 123 4567)',
    // Application Flow
    step: 'පියවර',
    of: '/',
    selectCountry: 'රට තෝරන්න',
    selectCountryDesc: 'ඔබට යන්න ඕනෑ රට තෝරන්න',
    uploadPassport: 'පාස්පෝට් එක',
    uploadPassportDesc: 'පාස්පෝට් එකේ ඡායාරූපයක් ගන්න',
    payment: 'ගෙවීම',
    paymentStepDesc: 'ගෙවීම සම්පූර්ණ කරන්න',
    done: 'අවසන්!',
    doneDesc: 'අයදුම්පත යොමු කළා',

    // Country Selection
    whereToTravel: 'ඔබට කොහෙට යන්න ඕනෑ?',
    india: 'ඉන්දියාව',
    indiaVisa: 'ඉන්දීය සංචාරක වීසා',
    multipleEntry: 'වසරක බහු ප්‍රවේශ',
    selectIndia: 'ඉන්දියාව තෝරන්න',
    comingSoon: 'ඉක්මනින්',
    moreCountriesSoon: 'තවත් රටවල් ඉක්මනින් එකතු වේ',
    continue: 'ඉදිරියට යන්න',
    back: 'ආපසු යන්න',

    // Passport Upload
    passportPhotoTitle: 'පාස්පෝට් එකේ ඡායාරූපයක් ගන්න',
    passportPhotoDesc: 'ඔබේ ඡායාරූපය සහ විස්තර ඇති පිටුව අවශ්‍යයි',
    tapToTakePhoto: 'ඡායාරූපයක් ගැනීමට මෙහි ඔබන්න',
    orUploadFile: 'හෝ ගොනුවක් තෝරන්න',
    photoTips: 'ඡායාරූප උපදෙස්',
    tip1: 'පාස්පෝට් එක සමතල මතුපිටක තබන්න',
    tip2: 'හොඳ ආලෝකයක් ඇති බවට වග බලන්න',
    tip3: 'පිටුවේ සියලුම කොන් ඇතුළත් කරන්න',
    tip4: 'සෙවනැලි සහ දිලිසීම වළක්වන්න',
    retakePhoto: 'නැවත ඡායාරූපයක් ගන්න',
    useThisPhoto: 'මෙම ඡායාරූපය භාවිතා කරන්න',
    scanPassport: 'ඔබේ පාස්පෝට් එක ස්කෑන් කරන්න',
    autoAlignDesc: 'ස්වයංක්‍රීයව පෙළගස්වා කපා දමයි',
    opensCameraApp: 'ඔබේ කැමරාව විවෘත කරයි',
    photoCaptured: 'ඡායාරූපය ලබා ගත්තා',
    passportExample: 'මේ ආකාරයට තබන්න:',
    placePassport: 'පාස්පෝට් එක රාමුව ඇතුළේ තබන්න',
    moveCloser: 'ළඟට යන්න',
    moreLightNeeded: 'තවත් ආලෝකය අවශ්‍යයි',
    holdSteady: 'ස්ථිරව අල්ලාගෙන ඉන්න',
    capture: 'ඡායාරූපය ගන්න',

    // Payment
    paymentTitle: 'ගෙවීම සම්පූර්ණ කරන්න',
    paymentDesc: 'අයදුම්පත යොමු කිරීමට ආරක්ෂිතව ගෙවන්න',
    totalAmount: 'මුළු මුදල',
    paymentMethods: 'ගෙවීම් ක්‍රම',
    bankTransfer: 'බැංකු මාරු',
    cashPayment: 'මුදල් ගෙවීම',
    paymentCard: 'කාඩ්පතෙන් ගෙවන්න',
    payWithCard: 'කාඩ්පතෙන් ගෙවන්න',
    payLater: 'අපේ කාර්යාලයට හෝ බැංකු මාරුව මගින් ගෙවන්න',
    submitApplication: 'අයදුම්පත යොමු කරන්න',
    cardSecure: 'Stripe මගින් ආරක්ෂිත ගෙවීම',
    cardPaymentError: 'කාඩ්පත් ගෙවීම අසාර්ථකයි. කරුණාකර නැවත උත්සාහ කරන්න.',
    bankPaymentError: 'ගෙවීම් ක්‍රමය යාවත්කාලීන කිරීම අසාර්ථකයි. කරුණාකර නැවත උත්සාහ කරන්න.',
    paymentConfirming: 'ඔබේ ගෙවීම තහවුරු කරමින්…',
    paymentSuccessToast: 'ගෙවීම සාර්ථකයි! ඔබේ අයදුම්පත යොමු කර ඇත.',
    paymentSuccessPopupTitle: 'ගෙවීම සාර්ථකයි',
    paymentSuccessPopupDesc: 'ඔබේ ගෙවීම සාර්ථකව සම්පූර්ණ විය.',
    refundPolicyTitle: 'මුදල් ආපසු ගෙවීමේ ප්‍රතිපත්තිය',
    refundPolicyIntro: 'විමාන ට්‍රැවල්ස් (Vimana Travels) හිදී, අපගේ වීසා සේවාවන්හි විනිවිදභාවය වෙනුවෙන් අපි කැපවී සිටිමු. ගෙවීම් සිදු කිරීමට පෙර කරුණාකර අපගේ මුදල් ආපසු ගෙවීමේ ප්‍රතිපත්තිය හොඳින් කියවන්න.',
    refundPolicyVisaRejection: 'වීසා ප්‍රතික්ෂේප වීමකදී මුදල් ආපසු ගෙවීම',
    refundPolicyVisaRejectionDesc: 'පාරිභෝගිකයෙකුගේ වීසා අයදුම්පත අදාළ තානාපති කාර්යාලය මගින් ප්‍රතික්ෂේප කරනු ලැබුවහොත්, මුදල් ආපසු ගෙවීම පහත පරිදි සිදු කෙරේ:',
    refundPolicyServiceFeeRefundable: 'අපගේ සේවා ගාස්තුව (Service Fee) ආපසු ගෙවනු ලැබේ',
    refundPolicyEmbassyFeeNonRefundable: 'තානාපති කාර්යාල / වීසා ගාස්තු ආපසු ගෙවනු නොලැබේ (Non-refundable). මන්ද මෙම ගාස්තු අදාළ බලධාරීන් වෙත සෘජුවම ගෙවනු ලබන අතර එය අපගේ පාලනයෙන් බැහැර කරුණකි',
    refundPolicyBreakdown: 'වීසා වර්ගය අනුව මුදල් ආපසු ගෙවීමේ විස්තරය',

    refundPolicy1YearTitle: 'වසර 1ක බහු-ඇතුල්වීමේ සංචාරක වීසා',
    refundPolicy1YearTotal: 'මුළු ගාස්තුව: රු. 14,900/-',
    refundPolicy1YearRefundable: 'ආපසු ගෙවන මුදල (සේවා ගාස්තුව): රු. 5,400/-',
    refundPolicy1YearNonRefundable: 'ආපසු නොගෙවන මුදල (වීසා ගාස්තුව): රු. 9,500/-',

    refundPolicy1MonthTitle: 'මාස 1ක තනි ඇතුල්වීමේ සංචාරක වීසා',
    refundPolicy1MonthTotal: 'මුළු ගාස්තුව: රු. 9,900/-',
    refundPolicy1MonthRefundable: 'ආපසු ගෙවන මුදල (සේවා ගාස්තුව): රු. 4,400/-',
    refundPolicy1MonthNonRefundable: 'ආපසු නොගෙවන මුදල (වීසා ගාස්තුව): රු. 5,500/-',

    refundPolicyImportantNotes: 'වැදගත් සටහන්',
    refundPolicyNote1: 'මුදල් ආපසු ගෙවීම් අදාළ වනුයේ වීසා ප්‍රතික්ෂේප වීමකදී පමණි',
    refundPolicyNote2: 'වීසා බලපත්‍රයක් අනුමත වූ පසු හෝ නිකුත් කළ පසු කිසිදු මුදලක් ආපසු ගෙවනු නොලැබේ',
    refundPolicyNote3: 'නිල වශයෙන් වීසා ප්‍රතික්ෂේප වූ බවට තහවුරු වීමෙන් පසු, සාධාරණ කාල රාමුවක් තුළ මුදල් ආපසු ගෙවීම සිදු කරනු ලැබේ',
    refundPolicyClarification: 'යම් ගැටළුවක් හෝ පැහැදිලි කරගැනීමක් ඇත්නම්, අයදුම් කිරීමට පෙර කරුණාකර අපගේ සහාය කණ්ඩායම (Support team) අමතන්න.',

    // Success
    successTitle: 'අයදුම්පත යොමු කළා!',
    successDesc: 'ඔබේ වීසා අයදුම්පත අපට ලැබුණා',
    applicationNumber: 'ඔබේ අයදුම්පත් අංකය',
    saveThisNumber: 'තත්ත්වය බැලීමට මෙම අංකය සුරකින්න',
    whatNext: 'ඊළඟට මොකද වෙන්නේ?',
    nextStep1: 'අපි ඔබේ අයදුම්පත සකසනවා',
    nextStep2: 'WhatsApp මගින් යාවත්කාලීන ලැබේ',
    nextStep3: 'පැය 72 කින් වීසාව ලැබේ',
    goHome: 'මුල් පිටුවට යන්න',

    // Status Check
    statusTitle: 'ඔබේ අයදුම්පත පරීක්ෂා කරන්න',
    statusDesc: 'තත්ත්වය පරීක්ෂා කිරීමට ඔබේ අයදුම්පත් අංකය ඇතුළත් කරන්න',
    enterPassportNumber: 'පාස්පෝට් අංකය ඇතුළත් කරන්න',
    passportNumberPlaceholder: 'උදා: N1234567',
    orUseAppNumber: 'හෝ අයදුම්පත් අංකය භාවිතා කරන්න',
    enterAppNumber: 'අයදුම්පත් අංකය ඇතුළත් කරන්න',
    appNumberPlaceholder: 'උදා., VIM-2024-001234',
    checkStatus: 'තත්ත්වය බලන්න',

    // Contact Info Collection
    contactInfoTitle: 'අපි ඔබව සම්බන්ධ කරගන්නේ කොහොමද?',
    contactInfoDesc: 'ඔබේ වීසාව ගැන යාවත්කාලීන එවන්නෙමු',
    emailLabel: 'ඊමේල් ලිපිනය',
    emailPlaceholder: 'your@email.com',
    phoneLabel: 'දුරකථන අංකය',
    phonePlaceholder: '07X XXX XXXX',
    atLeastOne: 'අවම වශයෙන් එක් සම්බන්ධතා ක්‍රමයක් ඇතුළත් කරන්න',
    statusPending: 'සකසමින්',
    statusApproved: 'අනුමත කළා',
    statusReady: 'ලබාගැනීමට සූදානම්',
    pendingDesc: 'ඔබේ අයදුම්පත සකසමින් පවතී',
    approvedDesc: 'ඔබේ වීසාව අනුමත කළා!',
    readyDesc: 'ඔබේ වීසාව ලැබේ. කරුණාකර එය ගන්න.',

    // FAQ
    faqTitle: 'නිතර අසන ප්‍රශ්න',
    faqSubtitle: 'පොදු ප්‍රශ්නවලට පිළිතුරු සොයන්න',
    faq1Q: 'වසර 1ක බහු-ඇතුල්වීමේ (Multiple Entry) සංචාරක වීසා යනු කුමක්ද?',
    faq1A: 'වසර 1ක බහු-ඇතුල්වීමේ සංචාරක වීසාවක් මගින්, එය නිකුත් කළ දින සිට වසරක් ඇතුළත ඉන්දියාවට කිහිප වතාවක් ගමන් කිරීමට ඔබට ඉඩ ලබා දේ. එහි වලංගු කාලය තුළ ඔබට අවශ්‍ය වාර ගණනක් ඉන්දියාවට ඇතුළු වීමට සහ පිටවීමට හැකි අතර, සෑම ගමනක්ම ආගමන විගමන නීතිවලට අනුකූල විය යුතුය.',

    faq2Q: 'මාස 1ක තනි ඇතුල්වීමේ (Single Entry) සංචාරක වීසා යනු කුමක්ද?',
    faq2A: 'මෙම වීසාව මගින් ඉන්දියාවට එක් වරක් පමණක් ඇතුළු වී අනුමත කාලය සඳහා රැඳී සිටීමට ඉඩ ලබා දේ. ඔබ ඉන්දියාවෙන් පිට වූ වහාම, මාස 1ක කාලය අවසන් වී නොතිබුණද මෙම වීසාව ඉබේම අවලංගු වේ.',

    faq3Q: 'ඔබ ව්‍යාපාරික වීසා (Business Visas) සකසා දෙනවාද?',
    faq3A: 'ඔව්. ඔබේ සුදුසුකම් සහ තානාපති කාර්යාල අවශ්‍යතා මත පදනම්ව අපි ව්‍යාපාරික වීසා සකසා දෙන්නෙමු. ඉදිරියට කටයුතු කිරීමට පෙර, ඔබේ සංචාරයේ අරමුණ සහ අවශ්‍ය ලියකියවිලි පරීක්ෂා කර බැලීම සඳහා කරුණාකර කල් ඇතිව අපව අමතන්න.',

    faq4Q: 'Multiple Entry වීසා බලපත්‍රයක් භාවිතයෙන් මට ඉන්දියාවේ ඕනෑම ගුවන් තොටුපළකින් හෝ වරායකින් ඇතුළු විය හැකිද?',
    faq4A: 'ඔව්. වලංගු Multiple Entry සංචාරක වීසා බලපත්‍රයක් සමඟ, ආගමන විගමන අනුමැතියට යටත්ව ඕනෑම බලයලත් ගුවන් තොටුපළක් හෝ වරායක් හරහා ඔබට ඉන්දියාවට ඇතුළු විය හැකිය.',

    faq5Q: 'වසර 1ක සංචාරක වීසා බලපත්‍රයක් භාවිතයෙන් මට ඉන්දියාවේ රැකියාවක් කළ හැකිද?',
    faq5A: 'නැත. සංචාරක වීසා මත ඉන්දියාවේ රැකියාවක නිරත වීම සපුරා තහනම්ය. සංචාරක වීසා නිකුත් කරනු ලබන්නේ විනෝද චාරිකා, මිතුරන් හෝ ඥාතීන් බැලීම සහ ව්‍යාපාරික නොවන වෙනත් කටයුතු සඳහා පමණි.',

    faq6Q: 'වීසා ලබා ගැනීමට කොපමණ කාලයක් ගතවේද?',
    faq6A: 'වීසා සැකසීම සඳහා සාමාන්‍යයෙන් අවම වශයෙන් වැඩ කරන දින 3ක් ගත වේ. නමුත් තානාපති කාර්යාලයේ ක්‍රියාවලීන් සහ අමතර පරීක්ෂා කිරීම් මත එය වැඩ කරන දින 5ක් හෝ ඊට වැඩි කාලයක් ගත විය හැක.',

    faq7Q: 'ඔබ ස්ටිකර් වීසා (Sticker Visas) ලබා දෙනවාද?',
    faq7A: 'නැත. දැනට අපි ස්ටිකර් වීසා සේවාව ලබා නොදෙමු. අපි සකසන සියලුම වීසා ඉලෙක්ට්‍රොනික Non-sticker වීසා වේ.',

    faq8Q: 'ඔබ ගුවන් ටිකට්පත් වෙන්කරවා ගැනීමට (Ticket Booking) උදව් කරනවාද?',
    faq8A: 'ඔව්. වීසා ලබා ගැනීම සඳහා අවශ්‍ය ටිකට්පත් සැකසීම ඇතුළුව, ගුවන් ටිකට්පත් වෙන්කරවා ගැනීමට අපි සහාය වන්නෙමු.',

    faq9Q: 'ඔබ වෙනත් රටවල් සඳහා වීසා සකසනවාද?',
    faq9A: 'නැත. දැනට අපි ඉන්දියාව සඳහා පමණක් වීසා සකසන්නෙමු. අනාගතයේදී වෙනත් රටවල් ඊට එකතු විය හැක.',

    faq10Q: 'වසර 1ක සංචාරක වීසා බලපත්‍රය සමඟ මට කී වතාවක් ඉන්දියාවට යා හැකිද?',
    faq10A: 'වසර 1ක වලංගු කාලය තුළ ඔබට නිශ්චිත සීමාවකින් තොරව කිහිප වතාවක් ගමන් කළ හැකිය. කෙසේ වෙතත්, එක් එක් රැඳී සිටීම සඳහා ඉන්දියානු ආගමන විගමන නීතිවලට අනුකූල විය යුතුය.',

    faq11Q: 'සංචාරක වීසා සඳහා අයදුම් කිරීමට අවශ්‍ය ලියකියවිලි මොනවාද?',
    faq11A: 'ඔබට අවශ්‍ය වන්නේ ඔබේ විදේශ ගමන් බලපත්‍රයේ ස්කෑන් පිටපතක් සහ මෑතකදී ගත් විදේශ ගමන් බලපත්‍ර ප්‍රමාණයේ ඡායාරූපයක්. තානාපති කාර්යාලයට අවශ්‍ය වුවහොත් අමතර ලියකියවිලි ඉල්ලා සිටිය හැක.',

    // Contact
    contactTitle: 'උදව් අවශ්‍යද?',
    contactDesc: 'අපි ඔබට උදව් කරන්න මෙහි ඉන්නවා',
    callUs: 'අපට කතා කරන්න',
    whatsappUs: 'WhatsApp කරන්න',
    visitOffice: 'අපේ කාර්යාලයට එන්න',
    officeHours: 'සඳුදා සිට සෙනසුරාදා, පෙ.ව. 9 - ප.ව. 6',
    phoneNumber: '076 873 4396',
    phoneNumberRaw: '94768734396',

    // Visa Application Form - Step Titles
    startApplicationTitle: 'අයදුම්පත ආරම්භ කරන්න',
    startApplicationDesc: 'කරුණාකර ඔබේ මූලික තොරතුරු සපයන්න',
    uploadDocumentsTitle: 'ලියකියවිලි එකතු කරන්න',
    uploadDocumentsDesc: 'ඔබේ විදේශ ගමන් බලපත්‍රය සහ ඡායාරූපය උඩුගත කරන්න',
    travelDetailsTitle: 'සංචාරක විස්තර',
    travelDetailsDesc: 'ඔබේ සංචාරක සැලසුම් ගැන අපට කියන්න',
    personalFamilyTitle: 'පුද්ගලික සහ පවුලේ විස්තර',
    personalFamilyDesc: 'ඔබ සහ ඔබේ පවුලේ තොරතුරු',
    addressEducationTitle: 'ලිපිනය, අධ්‍යාපනය සහ ආගම',
    addressEducationDesc: 'අමතර පුද්ගලික තොරතුරු',
    travelHistoryTitle: 'සංචාරක ඉතිහාසය',
    travelHistoryDesc: 'ඔබේ පෙර සංචාර පිළිබඳ තොරතුරු',
    referenceInIndiaTitle: 'ඉන්දියාවේ යොමුව',
    referenceInIndiaDesc: 'ඉන්දියාවේ ඔබේ යොමුවේ සම්බන්ධතා විස්තර',
    referenceNotRequiredDesc: 'සංචාරක සඳහා අවශ්‍ය නැත',
    reviewDeclarationTitle: 'සමාලෝචනය සහ ප්‍රකාශනය',
    reviewDeclarationDesc: 'යොමු කිරීමට පෙර ඔබේ අයදුම්පත සමාලෝචනය කරන්න',
    applicationIdCopyPrompt: 'මෙය පිටපත් කරගෙන, ඔබට වෙලාව ලැබෙන විට පෝරමය පිරවීම දිගටම කරගෙන යා හැක',
    

    // Step 1: Start Application
    applicationId: 'අයදුම්පත් අංකය',
    applicationIdWillBeGenerated: 'අයදුම්පත ඉදිරිපත් කිරීමෙන් පසු ඔබේ අයදුම්පත් අංකය ජනනය වේ',
    applicationOpen: 'අයදුම්පත විවෘතයි',
    fullNameLabel: 'සම්පූර්ණ නම (විදේශ ගමන් බලපත්‍රයේ සඳහන් පරිදි) *',
    fullNamePlaceholder: 'ඔබේ සම්පූර්ණ නම ඇතුළත් කරන්න',
    phoneNumberLabel: 'WhatsApp අංකය *',
    phoneNumberPlaceholder: '07X XXX XXXX',
    emailAddressLabel: 'විද්‍යුත් තැපැල් ලිපිනය (ඊමේල්) *',
    visaTypeLabel: 'වීසා වර්ගය තෝරන්න *',
    visaTypePlaceholder: 'වීසා වර්ගය තෝරන්න',
    visaType30Days: 'e-සංචාරක වීසා (දින 30ක් සඳහා)',
    visaType1Year: 'e-සංචාරක වීසා (වසර 1 ක්)',
    visaType5Years: 'e-සංචාරක වීසා (වසර 5 ක්)',
    visaTypeEBusiness1Year: 'e-ව්‍යාපාර වීසා (වසර 1 ක්)',
    businessTravelPurposeLabel: 'ඔබ ගමන් කිරීමට අවශ්‍ය අරමුණ කුමක්ද?',
    businessTravelPurposePlaceholder: 'අරමුණ තෝරන්න',
    businessTravelPurposePopupTitle: 'අවශ්‍ය ලේඛණ',
    ok: 'හරි',
    purpose_SET_UP_BUSINESS_VENTURE: 'කාර්මික/ව්‍යාපාර ව්‍යාපෘතියක් ආරම්භ කිරීමට',
    purpose_SALE_PURCHASE_TRADE: 'විකිණීම / මිලදී ගැනීම / වෙළඳාම',
    purpose_ATTEND_BUSINESS_MEETINGS: 'තාක්ෂණික / ව්‍යාපාර රැස්වීම්වලට සහභාගී වීමට',
    purpose_TO_RECRUIT_MANPOWER: 'කම්කරු බලය බඩු කිරීමට',
    purpose_EXHIBITIONS_TRADE_FAIRS: 'ප්‍රදර්ශන, ව්‍යාපාර / වෙළඳ මැණික් උළෙලවලට සහභාගී වීම',
    purpose_EXPERT_SPECIALIST_PROJECT: 'සිදුවෙමින් පවතින ව්‍යාපෘතියක් සම්බන්ධයෙන් විශේෂඥ / විශේෂඥ',
    purpose_CONDUCTING_TOURS: 'සංචාර පාලනය කිරීම',
    purpose_GIAN_LECTURES: 'ගෝලීය අධ්‍යාපනික ජාල ප්‍රවර්ධනය (GIAN) යටතේ දේශන ලබා දීමට',
    purpose_SPORTS_RELATED_ACTIVITY: 'ක්‍රීඩා සම්බන්ධ ක්‍රියාකාරකම්',
    purpose_TO_JOIN_THE_VESSEL: 'යාත්‍රාවට සම්බන්ධ වීමට',
    purposePopup_SET_UP_BUSINESS_VENTURE: 'මෙයට අයදුම් කිරීමට ඔබට ව්‍යාපාර ආරාධනාවක පිටපතක් අවශ්‍ය වේ, ව්‍යාපාර කාඩ්පතක් අවශ්‍ය වේ.',
    purposePopup_SALE_PURCHASE_TRADE: 'මෙයට අයදුම් කිරීමට ඔබට ව්‍යාපාර ආරාධනාවක පිටපතක් අවශ්‍ය වේ, ව්‍යාපාර කාඩ්පතක් අවශ්‍ය වේ.',
    purposePopup_ATTEND_BUSINESS_MEETINGS: 'මෙයට අයදුම් කිරීමට ඔබට ව්‍යාපාර ආරාධනාවක පිටපතක් අවශ්‍ය වේ, ව්‍යාපාර කාඩ්පතක් අවශ්‍ය වේ.',
    purposePopup_TO_RECRUIT_MANPOWER: 'මෙයට අයදුම් කිරීමට ඔබට ව්‍යාපාර ආරාධනාවක පිටපතක් අවශ්‍ය වේ, ව්‍යාපාර කාඩ්පතක් අවශ්‍ය වේ.',
    purposePopup_EXHIBITIONS_TRADE_FAIRS: 'මෙයට අයදුම් කිරීමට ඔබට ව්‍යාපාර ආරාධනාවක පිටපතක් අවශ්‍ය වේ, ව්‍යාපාර කාඩ්පතක් අවශ්‍ය වේ.',
    purposePopup_EXPERT_SPECIALIST_PROJECT: 'මෙයට අයදුම් කිරීමට ඔබට ව්‍යාපාර ආරාධනාවක පිටපතක් අවශ්‍ය වේ, ව්‍යාපාර කාඩ්පතක් අවශ්‍ය වේ.',
    purposePopup_CONDUCTING_TOURS: 'මෙයට අයදුම් කිරීමට ඔබට ව්‍යාපාර ආරාධනාවක පිටපතක් අවශ්‍ය වේ, ව්‍යාපාර කාඩ්පතක් අවශ්‍ය වේ.',
    purposePopup_GIAN_LECTURES: 'ඔබට අවශ්‍ය: විදේශීය ආචාර්යවරයාට ධාරක ආයතනයේ ආරාධනාව, IIT ඛරග්පූර් විසින් නිකුත් කරන ලද GIAN යටතේ අනුමතිය ඇණවුමේ පිටපත, ආචාර්යවරයා විසින් ගත කරන පාඨමාලාවල සාරාංශයේ පිටපත.',
    purposePopup_SPORTS_RELATED_ACTIVITY: 'ඔබට අවශ්‍ය: ඉන්දියා රජයේ තරුණ කටයුතු හා ක්‍රීඩා අමාත්‍යාංශය (ක්‍රීඩා දෙපාර්තමේන්තුව) අනුමැතිය, ඉන්දියාවට පිටරට ක්‍රීඩා කණ්ඩායම් සහ ක්‍රීඩකයින්ට පැමිණීමට අදාළ ඉන්දියානු ක්‍රීඩා සම්මේලනය/සංගමයේ ආරාධනා ලිපිය.',
    purposePopup_TO_JOIN_THE_VESSEL: 'ඔබට අවශ්‍ය: ඉන්දියාවේ පිහිටා ඇති ඉන්දියානු/විදේශීය නැව් මාර්ග ඒජන්ට් අංශයෙන් අනුබල ලිපිය, නැවියන්ගේ අඛණ්ඩ නිදහස් සහතිකය (CDC) පිටපත.',
    ebusinessDocsTitle: 'අනුබල ලේඛණ උඩුගත කරන්න',
    ebusinessDocsDesc: 'ඔබ තෝරාගත් ගමන් අරමුණට අවශ්‍ය PDF උඩුගත කරන්න.',
    uploadPdf: 'PDF උඩුගත කරන්න',
    docUploaded: 'උඩුගත කළා',
    replace: 'අලුත්වැඩියා කරන්න',
    ebusinessUploadError: 'ලේඛණය උඩුගත කිරීම අසාර්ථක විය. නැවත උත්සාහ කරන්න.',
    doc_BUSINESS_INVITATION: 'ව්‍යාපාර ආරාධනාව (පිටපත)',
    doc_BUSINESS_CARD: 'ව්‍යාපාර කාඩ්පත',
    doc_GIAN_INVITATION: 'ධාරක ආයතනයේ විදේශීය ආචාර්යවරයාට ආරාධනාව',
    doc_GIAN_SANCTION_ORDER: 'GIAN යටතේ අනුමතිය ඇණවුමේ පිටපත (IIT ඛරග්පූර්)',
    doc_GIAN_SYNOPSIS: 'ආචාර්යවරයා විසින් ගත කරන පාඨමාලාවල සාරාංශය',
    doc_SPORTS_APPROVAL: 'ඉන්දියා රජයේ අනුමැතිය (තරුණ කටයුතු හා ක්‍රීඩා අමාත්‍යාංශය)',
    doc_SPORTS_INVITATION_LETTER: 'ඉන්දියාවේ ක්‍රීඩා සම්මේලනය/සංගමයෙන් ආරාධනා ලිපිය',
    doc_VESSEL_SPONSORING_LETTER: 'ඉන්දියාවේ නැව් මාර්ග ඒජන්ට් අංශයෙන් අනුබල ලිපිය',
    doc_VESSEL_CDC: 'නැවියන්ගේ අඛණ්ඩ නිදහස් සහතිකය (CDC) පිටපත',

    // Step 2: Upload Documents
    passportMandatory: 'විදේශ ගමන් බලපත්‍රය (අනිවාර්යයි) *',
    photographMandatory: 'විදේශ ගමන් බලපත්‍ර ප්‍රමාණයේ ඡායාරූපය (අනිවාර්යයි) *',
    passportUploaded: 'පාස්පෝට් එක එකතු කළා',
    photographUploaded: 'ඡායාරූපය එකතු කළා',
    takePhoto: 'ඡායාරූපයක් ගන්න',
    uploadFile: 'හෝ ගොනුවක් උඩුගත කරන්න',
    remove: 'ඉවත් කරන්න',
    uploadPhotograph: 'ඡායාරූපය උඩුගත කරන්න',
    clickToSelectFile: 'ගොනුවක් තෝරාගැනීමට ක්ලික් කරන්න',
    passportValidityNote: '• විදේශ ගමන් බලපත්‍රය අවම වශයෙන් මාස 6ක් වත් වලංගු විය යුතුය',
    passportClearNote: '• පැහැදිලි රූපයක් විය යුතු අතර, සියලු කෙළවරවල් දර්ශනය විය යුතුය',
    photoFrontFacing: '• ඉදිරි දෙස, සරල පසුබිමක්',
    photoRecentNote: '• පැහැදිලි, මෑත කාලීන ඡායාරූපයක්',
    photoFormatJpeg: '• ආකෘතිය: JPEG',
    photoSizeRequirements: '• ප්ප්‍රමාණය: අවමය 10 KB, උපරිමය 1 MB',
    photoDimensionRequirements: '• අවම මානයන් පික්සල 350 (පළල) x පික්සල 350 (උස) විය යුතුය.',
    photoWhiteBackgroundNote: '• සුදු පසුබිමක් සහිත මෑතකදී ගත් මුහුණ ඉදිරියට පෙනෙන ඡායාරූපයක්.',

    // Step 3: Travel Details
    portOfArrivalLabel: 'පැමිණෙන වරාය (හෝ ගුවන් තොටුපළ) *',
    portOfArrivalPlaceholder: 'පැමිණීමේ වරාය තෝරන්න',
    expectedArrivalLabel: 'පැමිණීමට බලාපොරොත්තු වන දිනය *',
    placesToVisitLabel: 'නැරඹීමට අදහස් කරන ස්ථාන *',
    placesToVisitPlaceholder: 'උදා: දිල්ලි, අග්‍රා, ජයිපූර්, මුම්බායි',
    placesToVisitHelper: 'ඔබ නැරඹීමට සැලසුම් කරන නගර හෝ ස්ථාන ලැයිස්තුගත කරන්න',

    // Step 4: Personal & Family Details
    fatherDetails: 'පියාගේ විස්තර',
    fatherNameLabel: 'පියාගේ සම්පූර්ණ නම *',
    fatherNamePlaceholder: 'පියාගේ සම්පූර්ණ නම ඇතුළත් කරන්න',
    fatherBirthplaceLabel: 'පියා උපන් ස්ථානය *',
    fatherBirthplacePlaceholder: 'නගරය, රට',
    motherDetails: 'මවගේ විස්තර',
    motherNameLabel: 'මවගේ සම්පූර්ණ නම *',
    motherNamePlaceholder: 'මවගේ සම්පූර්ණ නම ඇතුළත් කරන්න',
    motherBirthplaceLabel: 'මව උපන් ස්ථානය *',
    motherBirthplacePlaceholder: 'නගරය, රට',
    maritalStatusLabel: 'විවාහක තත්ත්වය *',
    maritalStatusPlaceholder: 'විවාහක තත්ත්වය තෝරන්න',
    single: 'අවිවාහක',
    married: 'විවාහක',
    other: 'වෙනත්',
    spouseDetails: 'කලත්‍රයාගේ විස්තර',
    spouseNameLabel: 'කලත්‍රයාගේ සම්පූර්ණ නම *',
    spouseNamePlaceholder: 'කලත්‍රයාගේ සම්පූර්ණ නම ඇතුළත් කරන්න',
    spouseNationalityLabel: 'කලත්‍රයාගේ ජාතිකත්වය *',
    spouseNationalityPlaceholder: 'කලත්‍රයාගේ ජාතිකත්වය ඇතුළත් කරන්න',
    spouseBirthplaceLabel: 'කලත්‍රයා උපන් ස්ථානය *',
    spouseBirthplacePlaceholder: 'නගරය, රට',

    // Step 5: Address, Education & Religion
    residentialAddressLabel: 'අයදුම්කරුගේ පදිංචි ලිපිනය *',
    residentialAddressPlaceholder: 'ඔබේ සම්පූර්ණ පදිංචි ලිපිනය ඇතුළත් කරන්න',
    educationLabel: 'අධ්‍යාපන සුදුසුකම් *',
    educationPlaceholder: 'උදා: පශ්චාත් උපාධි',
    religionLabel: 'ආගම *',
    religionPlaceholder: 'ඔබේ ආගම ඇතුළත් කරන්න',
    educationalQualificationInstituteLabel: 'කොල්ලේජ්/විශ්වවිද්‍යාලයෙන් ලබාගත් අධ්‍යාපනික සුදුසුකම්?',
    educationalQualificationInstitutePlaceholder: 'උදා: කොළඹ විශ්වවිද්‍යාලය',

    // Step 6: Travel History
    visitedIndiaLabel: 'ඔබ මීට පෙර ඉන්දියාවට ගොස් තිබේද? *',
    citiesVisitedInIndiaLabel: 'ඉන්දියාවේ සංචාරය කළ නගර',
    citiesVisitedInIndiaPlaceholder: 'ඔබ ඉන්දියාවේ සංචාරය කළ නගර ලැයිස්තුගත කරන්න',
    previousVisaIdLabel: 'ඔබේ පෙර වීසා හැඳුනුම්පත් අංකය සඳහන් කරන්න',
    previousVisaIdPlaceholder: 'වීසා හැඳුනුම්පත් අංකය',
    visitedSAARCLabel: 'පසුගිය වසර 3 තුළ ඔබ SAARC රටවල සංචාරය කර තිබේද? *',
    saarcCountriesList: '(ඇෆ්ගනිස්ථානය, බංග්ලාදේශය, භූතානය, ඉන්දියාව, මාලදිවයින, නේපාලය, පකිස්ථානය)',
    overstayHistoryLabel: 'වීසා කාලය ඉක්මවා රැඳී සිටි ඉතිහාසයක් තිබේද? *',
    visaRejectionsLabel: 'මීට පෙර වීසා අයදුම්පත් ප්‍රතික්ෂේප වී තිබේද? *',
    refusedEntryLabel: 'කවදා හෝ කිසියම් රටකට ඇතුළුවීම ප්‍රතික්ෂේප වී තිබේද? *',
    arrestedConvictedLabel: 'කිසියම් රටක අධිකරණයක් මගින් ඔබව අත්අඩංගුවට ගැනීමක්, නඩු පැවරීමක් හෝ වරදකරු කිරීමක් සිදුවී තිබේද? *',
    traffickingCrimesLabel: 'ඔබ කවදා හෝ මිනිස් ජාවාරම් / මත්ද්‍රව්‍ය ජාවාරම් / ළමා අපචාර / කාන්තාවන්ට එරෙහි අපරාධ / ආර්ථික වැරදි / මූල්‍ය වංචා සඳහා සම්බන්ධ වී තිබේද? *',
    cyberTerrorViolenceLabel: 'ඔබ කවදා හෝ සයිබර් අපරාධ / ත්‍රස්තවාදී ක්‍රියා / කඩාකප්පල්කාරී ක්‍රියා / ඔත්තු බැලීම් / ජන සංහාර / දේශපාලන ඝාතන / වෙනත් ප්‍රචණ්ඩ ක්‍රියා සඳහා සම්බන්ධ වී තිබේද? *',
    glorifyTerrorismLabel: 'ඔබ කවදා හෝ කිසියම් මාධ්‍යයක් ඔස්සේ, ත්‍රස්තවාදී ප්‍රචණ්ඩත්වය සාධාරණීකරණය කරන, අගය කරන හෝ වෙනත් අයව ත්‍රස්තවාදී ක්‍රියාවලට හෝ බරපතල අපරාධවලට පොළඹවන අදහස් ප්‍රකාශ කර තිබේද? *',
    soughtAsylumLabel: 'ඔබ කිසියම් රටක දේශපාලනික හෝ වෙනත් රැකවරණයක් (Asylum) ඉල්ලා තිබේද? *',
    travelExplanationLabel: 'කරුණාකර පැහැදිලි කිරීමක් සපයන්න',
    travelExplanationPlaceholder: 'තත්ත්වයන් පිළිබඳ විස්තර සපයන්න',
    overstayExplanationLabel: 'කරුණාකර ඉක්මවා රැඳී සිටීම සඳහා පැහැදිලි කිරීමක් සපයන්න',
    overstayExplanationPlaceholder: 'ඉක්මවා රැඳී සිටීමේ තත්ත්වයන් පිළිබඳ විස්තර සපයන්න',
    visaRejectionExplanationLabel: 'කරුණාකර වීසා ප්‍රතික්ෂේප කිරීම සඳහා පැහැදිලි කිරීමක් සපයන්න',
    visaRejectionExplanationPlaceholder: 'වීසා ප්‍රතික්ෂේප කිරීමේ තත්ත්වයන් පිළිබඳ විස්තර සපයන්න',
    refusedEntryExplanationLabel: 'කරුණාකර ඇතුළුවීම ප්‍රතික්ෂේප කිරීම සඳහා පැහැදිලි කිරීමක් සපයන්න',
    refusedEntryExplanationPlaceholder: 'ඇතුළුවීම ප්‍රතික්ෂේප කිරීමේ තත්ත්වයන් පිළිබඳ විස්තර සපයන්න',

    // Step 7: Reference in India
    referenceNotRequiredTourism: 'සංචාරක සඳහා ඉන්දියාවේ යොමුවක් අවශ්‍ය නැත.',
    proceedNextStep: 'ඔබට ඊළඟ පියවරට යා හැකිය.',
    referenceNameLabel: 'ඉන්දියාවේ ඥාතියාගේ / මිතුරාගේ නම *',
    referenceNamePlaceholder: 'යොමු නම ඇතුළත් කරන්න',
    referenceAddressLabel: 'ලිපිනය *',
    referenceAddressPlaceholder: 'ඉන්දියාවේ සම්පූර්ණ ලිපිනය ඇතුළත් කරන්න',
    referencePhoneLabel: 'දුරකථන අංකය *',
    referencePhonePlaceholder: '+91 XXXXXXXXXX',

    // Step 8: Review & Declaration
    applicationSummary: 'අයදුම්පත් සාරාංශය',
    personalInformation: 'පුද්ගලික තොරතුරු',
    documentsUploaded: 'ලියකියවිලි එකතු කළා',
    travelDetails: 'සංචාරක විස්තර',
    personalFamilyDetails: 'පුද්ගලික සහ පවුල් විස්තර',
    additionalInformation: 'අතිරේක තොරතුරු',
    arrival: 'පැමිණීම',
    port: 'වරාය',
    father: 'පියා',
    mother: 'මව',
    maritalStatusValue: 'තත්ත්වය',
    educationValue: 'අධ්‍යාපනය',
    religionValue: 'ආගම',
    declarationText: 'ඉහත සඳහන් තොරතුරු මාගේ උපරිම දැනුම හා විශ්වාසය අනුව සත්‍ය හා නිවැරදි බව මින් ප්‍රකාශ කරමි. ඉහත තොරතුරු කිසිවක් අසත්‍ය හෝ නොමඟ යවන සුළු බව සොයා ගතහොත්, ඒ සඳහා මම වගකිව යුතු බව දනිමි.',
    acceptDeclarationPrompt: 'ඔබේ අයදුම්පත යොමු කිරීමට කරුණාකර ප්‍රකාශනය පිළිගන්න.',

    // Navigation & Actions
    stepOf: 'පියවර {step} / {total}',
    submitButton: 'අයදුම්පත යොමු කරන්න',
    processing: 'සකසමින්...',

    // Common
    loading: 'පූරණය වෙමින්...',
    error: 'යමක් වැරදී ගියා',
    tryAgain: 'කරුණාකර නැවත උත්සාහ කරන්න',
    cancel: 'අවලංගු කරන්න',
    confirm: 'තහවුරු කරන්න',
    yes: 'ඔව්',
    no: 'නැත',
    close: 'වසන්න',

    // Language
    language: 'භාෂාව',
    english: 'English',
    sinhala: 'සිංහල',
    tamil: 'தமிழ்',
    selectLanguageDesc: 'කරුණාකර අයදුම්පත සඳහා ඔබේ කැමති භාෂාව තෝරන්න',
  },
  ta: {
    // App
    appName: 'விமான டிராவல்ஸ்',
    tagline: 'உங்கள் நம்பகமான விசா சேவை பங்குதாரர்',

    // Navigation
    home: 'முகப்பு',
    apply: 'விண்ணப்பிக்கவும்',
    status: 'நிலையை சரிபார்க்கவும்',
    faq: 'உதவி & கேள்விகள்',
    contact: 'தொடர்பு கொள்ளவும்',

    // Home Screen
    welcomeTitle: 'இந்திய விசாவை பெறுங்கள்',
    welcomeSubtitle: 'எளிது. வேகமானது. நம்பகமானது.',
    heroDescription: 'இந்தியாவிற்கான 1 வருட பல நுழைவு சுற்றுலா விசா',
    processingTime: '72 மணி நேரத்தில் தயார்',
    price: 'ரூ. 14,900',
    price30Days: 'ரூ. 9,900',
    price1Year: 'ரூ. 14,900',
    priceEBusiness1Year: 'ரூ. 15,000',
    startApplication: 'விண்ணப்பத்தை தொடங்குங்கள்',
    getVisaNow: 'இப்போது விசா பெறுங்கள்',
    needHelpWhatsApp: 'உதவி தேவையா? WhatsApp செய்யுங்கள்',
    checkExistingStatus: 'இருக்கும் விண்ணப்ப நிலையை பார்க்கவும்',
    continueApplication: 'விண்ணப்பத்தைத் தொடரவும்',
    continueApplicationDesc: 'நீங்கள் நிறுத்திய இடத்திலிருந்து தொடர உங்கள் விண்ணப்ப எண்ணை உள்ளிடவும்.',
    continueApplicationPlaceholder: 'எ.கா. VIM-2026-171522',
    continueApplicationSubmit: 'தொடரவும்',
    continueApplicationEnterNumber: 'உங்கள் விண்ணப்ப எண்ணை உள்ளிடவும்',
    continueApplicationNotFound: 'விண்ணப்பம் காணப்படவில்லை. எண்ணை சரிபார்த்து மீண்டும் முயற்சிக்கவும்.',
    continueApplicationAlreadySubmitted: 'இந்த விண்ணப்பம் ஏற்கனவே சமர்ப்பிக்கப்பட்டது. பார்க்க நிலை சரிபார்க்க பயன்படுத்தவும்.',

    // Features
    feature1Title: 'எளிய செயல்முறை',
    feature1Desc: '7 எளிய படிகளில் உங்கள் விசாவை பெறுங்கள்',
    feature2Title: 'வேகமான சேவை',
    feature2Desc: 'உங்கள் விசாவிற்காக 24 மணி நேரத்திற்குள் நாங்கள் விண்ணப்பிக்கிறோம்.',
    feature3Title: 'நிபுணர் ஆதரவு',
    feature3Desc: 'ஒவ்வொரு படியிலும் நிபுணர்களின் வழிகாட்டல்',

    // Application Flow
    step: 'படி',
    of: 'இல்',
    selectCountry: 'நாட்டைத் தேர்ந்தெடுக்கவும்',
    selectCountryDesc: 'நீங்கள் செல்ல விரும்பும் நாட்டைத் தேர்ந்தெடுக்கவும்',
    uploadPassport: 'கடவுச்சீட்டு பதிவேற்றம்',
    uploadPassportDesc: 'உங்கள் கடவுச்சீட்டின் புகைப்படத்தை பதிவேற்றவும்',
    payment: 'பணம் செலுத்துதல்',
    paymentStepDesc: 'பணம் செலுத்தலை நிறைவு செய்யவும்',
    done: 'முடிந்தது!',
    doneDesc: 'விண்ணப்பம் வெற்றிகரமாக சமர்ப்பிக்கப்பட்டது',

    // Country Selection
    whereToTravel: 'நீங்கள் எங்கு பயணம் செய்ய விரும்புகிறீர்கள்?',
    india: 'இந்தியா',
    indiaVisa: 'இந்திய சுற்றுலா விசா',
    multipleEntry: '1 வருட பல நுழைவு',
    selectIndia: 'இந்தியாவைத் தேர்ந்தெடுக்கவும்',
    comingSoon: 'விரைவில்',
    moreCountriesSoon: 'மேலும் நாடுகள் விரைவில் சேர்க்கப்படும்',
    continue: 'தொடரவும்',
    back: 'திரும்ப செல்லவும்',

    // Passport Upload
    passportPhotoTitle: 'உங்கள் கடவுச்சீட்டின் புகைப்படத்தை எடுக்கவும்',
    passportPhotoDesc: 'புகைப்படம் மற்றும் விவரங்கள் உள்ள பக்கம் தேவை',
    tapToTakePhoto: 'புகைப்படம் எடுக்க இங்கே தட்டவும்',
    orUploadFile: 'அல்லது கோப்புகளில் இருந்து பதிவேற்றவும்',
    photoTips: 'புகைப்பட குறிப்புகள்',
    tip1: 'கடவுச்சீட்டை சமமான மேற்பரப்பில் வைக்கவும்',
    tip2: 'போதிய வெளிச்சம் இருப்பதை உறுதி செய்யவும்',
    tip3: 'பக்கத்தின் நான்கு மூலைகளும் தெளிவாக இருக்க வேண்டும்',
    tip4: 'நிழல் மற்றும் பிரதிபலிப்பை தவிர்க்கவும்',
    retakePhoto: 'மீண்டும் புகைப்படம் எடுக்கவும்',
    useThisPhoto: 'இந்த புகைப்படத்தை பயன்படுத்தவும்',
    scanPassport: 'கடவுச்சீட்டை ஸ்கேன் செய்யவும்',
    autoAlignDesc: 'தானாக சீரமைத்து வெட்டப்படும்',
    opensCameraApp: 'கேமரா திறக்கப்படும்',
    photoCaptured: 'புகைப்படம் எடுக்கப்பட்டது',
    passportExample: 'இவ்வாறு வைக்கவும்:',
    placePassport: 'சட்டத்தின் உள்ளே கடவுச்சீட்டை வைக்கவும்',
    moveCloser: 'சற்றே அருகில் நகரவும்',
    moreLightNeeded: 'மேலும் வெளிச்சம் தேவை',
    holdSteady: 'நிலையாக பிடியுங்கள்',
    capture: 'புகைப்படம் எடுக்கவும்',

    // Payment
    paymentTitle: 'பணம் செலுத்தலை நிறைவு செய்யவும்',
    paymentDesc: 'உங்கள் விண்ணப்பத்தை சமர்ப்பிக்க பாதுகாப்பாக பணம் செலுத்துங்கள்',
    totalAmount: 'மொத்த தொகை',
    paymentMethods: 'பணம் செலுத்தும் முறைகள்',
    bankTransfer: 'வங்கி பரிமாற்றம்',
    cashPayment: 'பணம் (Cash)',
    paymentCard: 'கார்ட் மூலம் செலுத்து',
    payWithCard: 'கார்ட் மூலம் செலுத்து',
    payLater: 'அலுவலகத்தில் அல்லது வங்கி மூலம் செலுத்தலாம்',
    submitApplication: 'விண்ணப்பத்தை சமர்ப்பிக்கவும்',
    cardSecure: 'Stripe மூலம் பாதுகாப்பான பணம் செலுத்துதல்',
    cardPaymentError: 'கார்டு பணம் செலுத்துதல் தோல்வி. மீண்டும் முயற்சிக்கவும்.',
    bankPaymentError: 'பணம் செலுத்தும் முறை புதுப்பிக்க தோல்வி. மீண்டும் முயற்சிக்கவும்.',
    paymentConfirming: 'உங்கள் பணம் செலுத்துதல் உறுதிசெய்யப்படுகிறது…',
    paymentSuccessToast: 'பணம் செலுத்துதல் வெற்றி! உங்கள் விண்ணப்பம் சமர்ப்பிக்கப்பட்டது.',
    paymentSuccessPopupTitle: 'பணம் செலுத்துதல் வெற்றி',
    paymentSuccessPopupDesc: 'உங்கள் பணம் செலுத்துதல் வெற்றிகரமாக முடிக்கப்பட்டது.',
    refundPolicyTitle: 'பணம் திரும்பப் பெறும் கொள்கை',
    refundPolicyIntro: 'விமானா டிராவல்ஸ் (Vimana Travels) இல், எங்கள் விசா சேவைகளில் வெளிப்படைத்தன்மையுடன் செயல்பட நாங்கள் உறுதிபூண்டுள்ளோம். கட்டணம் செலுத்துவதற்கு முன், தயவுசெய்து எங்கள் பணம் திரும்பப் பெறும் கொள்கையைக் கவனமாகப் படிக்கவும்.',
    refundPolicyVisaRejection: 'விசா நிராகரிக்கப்பட்டால் பணம் திரும்பப் பெறுதல்',
    refundPolicyVisaRejectionDesc: 'ஒரு வாடிக்கையாளரின் விசா விண்ணப்பம் சம்பந்தப்பட்ட தூதரகத்தால் நிராகரிக்கப்பட்டால், பணம் திரும்பப் பெறுதல் பின்வருமாறு கையாளப்படும்:',
    refundPolicyServiceFeeRefundable: 'எங்கள் சேவைக் கட்டணம் (Service Fee) திரும்பப் பெறத்தக்கது',
    refundPolicyEmbassyFeeNonRefundable: 'தூதரக / விசா கட்டணங்கள் திரும்பப் பெறப்படமாட்டாது. ஏனெனில் இவை நேரடியாக சம்பந்தப்பட்ட அதிகாரசபைக்குச் செலுத்தப்படுவதால் அவை எங்கள் கட்டுப்பாட்டிற்கு அப்பாற்பட்டவை',
    refundPolicyBreakdown: 'விசா வகை வாரியாக திரும்பப் பெறும் விபரம்',

    refundPolicy1YearTitle: '1 வருட பல முறை நுழைவு சுற்றுலா விசா',
    refundPolicy1YearTotal: 'மொத்தக் கட்டணம்: LKR 14,900/-',
    refundPolicy1YearRefundable: 'திரும்பப் பெறக்கூடியது (சேவைக் கட்டணம்): LKR 5,400/-',
    refundPolicy1YearNonRefundable: 'திரும்பப் பெற முடியாதது (விசா கட்டணம்): LKR 9,500/-',

    refundPolicy1MonthTitle: '1 மாத ஒற்றை நுழைவு சுற்றுலா விசா',
    refundPolicy1MonthTotal: 'மொத்தக் கட்டணம்: LKR 9,900/-',
    refundPolicy1MonthRefundable: 'திரும்பப் பெறக்கூடியது (சேவைக் கட்டணம்): LKR 4,400/-',
    refundPolicy1MonthNonRefundable: 'திரும்பப் பெற முடியாதது (விசா கட்டணம்): LKR 5,500/-',

    refundPolicyImportantNotes: 'முக்கியமான குறிப்புகள்',
    refundPolicyNote1: 'விசா நிராகரிக்கப்பட்டால் (Rejection) மட்டுமே பணம் திரும்பப் பெறுதல் பொருந்தும்',
    refundPolicyNote2: 'விசா அங்கீகரிக்கப்பட்டாலோ அல்லது வழங்கப்பட்டாலோ எந்தப் பணமும் திரும்ப வழங்கப்படாது',
    refundPolicyNote3: 'அதிகாரப்பூர்வ நிராகரிப்பு உறுதிப்படுத்தலைப் பெற்ற பிறகு, நியாயமான காலக்கெடுவுக்குள் பணம் திரும்பப் பெறும் செயல்முறை மேற்கொள்ளப்படும்',
    refundPolicyClarification: 'ஏதேனும் விளக்கம் தேவைப்பட்டால், உங்கள் விண்ணப்பத்தைச் சமர்ப்பிக்கும் முன் தயவுசெய்து எங்கள் ஆதரவுக் குழுவை (Support team) தொடர்பு கொள்ளவும்.',

    // Success
    successTitle: 'விண்ணப்பம் சமர்ப்பிக்கப்பட்டது!',
    successDesc: 'உங்கள் விசா விண்ணப்பத்தை பெற்றுள்ளோம்',
    applicationNumber: 'உங்கள் விண்ணப்ப எண்',
    saveThisNumber: 'நிலையை சரிபார்க்க இந்த எண்ணை சேமித்து வைக்கவும்',
    whatNext: 'அடுத்ததாக என்ன நடக்கும்?',
    nextStep1: 'உங்கள் விண்ணப்பம் செயலாக்கப்படும்',
    nextStep2: 'WhatsApp மூலம் தகவல்கள் அனுப்பப்படும்',
    nextStep3: '72 மணி நேரத்தில் விசா தயார்',
    goHome: 'முகப்புக்கு திரும்பவும்',

    // Status Check
    statusTitle: 'விண்ணப்ப நிலையை சரிபார்க்கவும்',
    statusDesc: 'உங்கள் விண்ணப்ப எண்ணை உள்ளிட்டு அதன் நிலையைப் பார்க்கவும்',
    enterPassportNumber: 'கடவுச்சீட்டு எண்ணை உள்ளிடவும்',
    passportNumberPlaceholder: 'உதா: N1234567',
    orUseAppNumber: 'அல்லது விண்ணப்ப எண்ணை பயன்படுத்தவும்',
    enterAppNumber: 'விண்ணப்ப எண்ணை உள்ளிடவும்',
    appNumberPlaceholder: 'உதா: VIM-2024-001234',
    checkStatus: 'நிலையை சரிபார்க்கவும்',

    // Contact Info Collection
    contactInfoTitle: 'உங்களை எவ்வாறு தொடர்பு கொள்ளலாம்?',
    contactInfoDesc: 'விசா தொடர்பான தகவல்களை அனுப்புவோம்',
    emailLabel: 'மின்னஞ்சல் முகவரி',
    emailPlaceholder: 'your@email.com',
    phoneLabel: 'தொலைபேசி எண்',
    phonePlaceholder: '07X XXX XXXX',
    phonenumberhelper: 'உங்கள் தொலைபேசி எண்ணை உள்ளிடவும் (உதா: 077 123 4567 அல்லது 77 123 4567)',

    atLeastOne: 'குறைந்தது ஒரு தொடர்பு முறையை உள்ளிடவும்',
    statusPending: 'செயலாக்கத்தில் உள்ளது',
    statusApproved: 'அங்கீகரிக்கப்பட்டது',
    statusReady: 'பெற தயாராக உள்ளது',
    pendingDesc: 'உங்கள் விண்ணப்பம் செயலாக்கத்தில் உள்ளது',
    approvedDesc: 'உங்கள் விசா அங்கீகரிக்கப்பட்டுள்ளது!',
    readyDesc: 'உங்கள் விசா தயாராக உள்ளது. தயவுசெய்து பெற்றுக்கொள்ளவும்.',

    // FAQ
    faqTitle: 'அடிக்கடி கேட்கப்படும் கேள்விகள்',
    faqSubtitle: 'பொதுவான கேள்விகளுக்கான பதில்கள்',
    faq1Q: '1 வருட பல முறை நுழைவு சுற்றுலா விசா (1 Year Multiple Entry Tourist Visa) என்றால் என்ன?',
    faq1A: 'இது விசா வழங்கப்பட்ட தேதியிலிருந்து ஒரு வருடத்திற்குள் பல முறை இந்தியாவுக்குச் செல்ல உங்களை அனுமதிக்கிறது. அதன் செல்லுபடியாகும் காலத்திற்குள் நீங்கள் விரும்பும் எத்தனை முறையும் இந்தியாவுக்குள் நுழையவும் வெளியேறவும் முடியும், ஆனால் ஒவ்வொரு முறையும் தங்கும் போது குடிவரவு விதிகளைப் பின்பற்ற வேண்டும்.',

    faq2Q: '1 மாத ஒற்றை நுழைவு சுற்றுலா விசா (1 Month Single Entry Tourist Visa) என்றால் என்ன?',
    faq2A: 'இந்த விசா மூலம் நீங்கள் இந்தியாவுக்குள் ஒரு முறை மட்டுமே நுழைந்து அனுமதிக்கப்பட்ட காலத்திற்குத் தங்க முடியும். நீங்கள் இந்தியாவை விட்டு வெளியேறியவுடன், 1 மாத காலம் முடிவடையாவிட்டாலும் கூட, அந்த விசா செல்லுபடியற்றதாகிவிடும்.',

    faq3Q: 'நீங்கள் வணிக விசாக்களை (Business Visas) செயல்முறைப்படுத்துகிறீர்களா?',
    faq3A: 'ஆம். தகுதி மற்றும் தூதரகத் தேவைகளுக்கு உட்பட்டு நாங்கள் வணிக விசாக்களைச் செய்து தருகிறோம். தொடர்வதற்கு முன், உங்கள் பயணத்தின் நோக்கம் மற்றும் தேவையான ஆவணங்களை நாங்கள் மதிப்பாய்வு செய்ய ஏதுவாக, தயவுசெய்து எங்களை முன்கூட்டியே தொடர்பு கொள்ளவும்.',

    faq4Q: 'Multiple Entry விசாவைப் பயன்படுத்தி நான் இந்தியாவின் எந்த விமான நிலையத்திலும் அல்லது துறைமுகத்திலும் இறங்க முடியுமா?',
    faq4A: 'ஆம். செல்லுபடியாகும் Multiple Entry சுற்றுலா விசா மூலம், குடிவரவு ஒப்புதலுக்கு உட்பட்டு, அங்கீகரிக்கப்பட்ட எந்த விமான நிலையம் அல்லது துறைமுகம் வழியாகவும் நீங்கள் இந்தியாவுக்குள் நுழையலாம்.',

    faq5Q: '1 வருட சுற்றுலா விசாவைப் பயன்படுத்தி நான் இந்தியாவில் வேலை செய்ய முடியுமா?',
    faq5A: 'இல்லை. சுற்றுலா விசாவில் இந்தியாவில் வேலை செய்யக் கண்டிப்பாக அனுமதி இல்லை. சுற்றுலா விசாக்கள் பயணம், இடங்களைச் சுற்றிப் பார்ப்பது, நண்பர்கள் அல்லது உறவினர்களைப் பார்ப்பது போன்ற வணிகம் அல்லாத நோக்கங்களுக்காக மட்டுமே வழங்கப்படுகின்றன.',

    faq6Q: 'விசா கிடைக்க எவ்வளவு காலம் ஆகும்?',
    faq6A: 'விசா செயல்முறைக்கு வழக்கமாக குறைந்தபட்சம் 3 வேலை நாட்கள் எடுக்கும். ஆனால் தூதரகத்தின் செயல்முறை நேரம் மற்றும் கூடுதல் சரிபார்ப்புகளைப் பொறுத்து 5 அல்லது அதற்கு மேற்பட்ட வேலை நாட்கள் ஆகலாம்.',

    faq7Q: 'நீங்கள் ஸ்டிக்கர் விசா (Sticker Visas) வழங்குகிறீர்களா?',
    faq7A: 'இல்லை. தற்சமயம் நாங்கள் ஸ்டிக்கர் விசாக்களை வழங்குவதில்லை. நாங்கள் செயல்முறைப்படுத்தும் அனைத்து விசாக்களும் ஸ்டிக்கர் அல்லாத மின்னணு விசாக்களாகும்.',

    faq8Q: 'விமான டிக்கெட் முன்பதிவு செய்ய (Flight Ticket Booking) நீங்கள் உதவுகிறீர்களா?',
    faq8A: 'ஆம். விசா நோக்கங்களுக்காகத் தேவைப்படும் டிக்கெட் ஏற்பாடுகள் உட்பட, விமான டிக்கெட் முன்பதிவு செய்வதற்கும் நாங்கள் உதவுகிறோம்.',

    faq9Q: 'மற்ற நாடுகளுக்கும் விசா செய்து தருகிறீர்களா?',
    faq9A: 'இல்லை. தற்போது நாங்கள் இந்தியாவுக்கான விசாக்களை மட்டுமே செய்கிறோம். எதிர்காலத்தில் மற்ற நாடுகள் சேர்க்கப்படலாம்.',

    faq10Q: '1 வருட பல முறை நுழைவு விசா மூலம் நான் எத்தனை முறை இந்தியாவுக்குச் செல்ல முடியும்?',
    faq10A: '1 வருட காலத்திற்குள் நீங்கள் குறிப்பிட்ட வரம்பின்றி பல முறை பயணம் செய்யலாம். இருப்பினும், ஒவ்வொரு முறை தங்கும் போதும் இந்தியக் குடிவரவு விதிகளுக்குக் கட்டுப்பட வேண்டும்.',

    faq11Q: 'சுற்றுலா விசாவுக்கு விண்ணப்பிக்க என்ன ஆவணங்கள் தேவை?',
    faq11A: 'உங்களுக்குத் தேவையானது உங்கள் கடவுச்சீட்டின் ஸ்கேன் செய்யப்பட்ட நகல் மற்றும் சமீபத்திய பாஸ்போர்ட் அளவு புகைப்படம். தூதரகத்திற்குத் தேவைப்பட்டால் கூடுதல் ஆவணங்கள் கோரப்படலாம்.',

    // Contact
    contactTitle: 'உதவி தேவையா?',
    contactDesc: 'உங்களுக்கு உதவ நாங்கள் தயாராக இருக்கிறோம்',
    callUs: 'எங்களை அழைக்கவும்',
    whatsappUs: 'WhatsApp செய்யுங்கள்',
    visitOffice: 'அலுவலகத்திற்கு வருக',
    officeHours: 'திங்கள் முதல் சனி | காலை 9 மணி முதல் மாலை 6 மணி வரை',
    phoneNumber: '077 111 3789',
    phoneNumberRaw: '94771113789',

    // Visa Application Form - Step Titles
    startApplicationTitle: 'விண்ணப்பத்தைத் தொடங்குங்கள்',
    startApplicationDesc: 'உங்கள் அடிப்படை தகவல்களை வழங்கவும்',
    uploadDocumentsTitle: 'ஆவணங்களை பதிவேற்றவும்',
    uploadDocumentsDesc: 'கடவுச்சீட்டு புகைப்படம் மற்றும் உங்கள் புகைப்படத்தை பதிவேற்றவும்',
    travelDetailsTitle: 'பயண விவரங்கள்',
    travelDetailsDesc: 'உங்கள் பயண திட்டங்களை எங்களிடம் கூறுங்கள்',
    personalFamilyTitle: 'தனிப்பட்ட & குடும்ப விவரங்கள்',
    personalFamilyDesc: 'உங்களைப் பற்றிய மற்றும் உங்கள் குடும்பத்தைப் பற்றிய தகவல்',
    addressEducationTitle: 'முகவரி, கல்வி & மதம்',
    addressEducationDesc: 'கூடுதல் தனிப்பட்ட தகவல்',
    travelHistoryTitle: 'பயண வரலாறு',
    travelHistoryDesc: 'உங்கள் முந்தைய பயணங்கள் பற்றிய தகவல்',
    referenceInIndiaTitle: 'இந்தியாவில் குறிப்பு',
    referenceInIndiaDesc: 'இந்தியாவில் உள்ள உங்கள் குறிப்பின் தொடர்பு விவரங்கள்',
    referenceNotRequiredDesc: 'சுற்றுலாவிற்கு தேவையில்லை',
    reviewDeclarationTitle: 'ஆய்வு & அறிவிப்பு',
    reviewDeclarationDesc: 'சமர்ப்பிக்கும் முன் உங்கள் விண்ணப்பத்தை ஆய்வு செய்யுங்கள்',
    applicationIdCopyPrompt: 'இதை நகலெடுத்து வைத்துக் கொண்டு, உங்களுக்கு நேரம் கிடைக்கும் போது படிவத்தை தொடரலாம்',

    // Step 1: Start Application
    applicationId: 'விண்ணப்ப எண்',
    applicationIdWillBeGenerated: 'உங்கள் விண்ணப்ப எண் சமர்ப்பிக்கும்போது உருவாக்கப்படும்',
    applicationOpen: 'விண்ணப்பம் திறந்துள்ளது',
    fullNameLabel: 'முழு பெயர் (கடவுச்சீட்டில் உள்ளபடி) *',
    fullNamePlaceholder: 'உங்கள் முழு பெயரை உள்ளிடவும்',
    phoneNumberLabel: 'வாட்ஸ்அப் எண் *',
    phoneNumberPlaceholder: '07X XXX XXXX',
    emailAddressLabel: 'மின்னஞ்சல் முகவரி *',
    visaTypeLabel: 'விசா வகையை தேர்ந்தெடுக்கவும் *',
    visaTypePlaceholder: 'விசா வகையை தேர்ந்தெடுக்கவும்',
    visaType30Days: 'eTourist Visa (30 நாட்கள்)',
    visaType1Year: 'eTourist Visa (1 வருடம்)',
    visaType5Years: 'eTourist Visa (5 வருடங்கள்)',
    visaTypeEBusiness1Year: 'eBusiness Visa (1 வருடம்)',
    businessTravelPurposeLabel: 'நீங்கள் பயணிக்க விரும்பும் நோக்கம் எது?',
    businessTravelPurposePlaceholder: 'நோக்கத்தை தேர்ந்தெடுக்கவும்',
    businessTravelPurposePopupTitle: 'தேவையான ஆவணங்கள்',
    ok: 'சரி',
    purpose_SET_UP_BUSINESS_VENTURE: 'தொழில்துறை/வணிக முயற்சியை அமைக்க',
    purpose_SALE_PURCHASE_TRADE: 'விற்பனை / கொள்முதல் / வர்த்தகம்',
    purpose_ATTEND_BUSINESS_MEETINGS: 'தொழில்நுட்ப/வணிக சந்திப்புகளில் கலந்துகொள்ள',
    purpose_TO_RECRUIT_MANPOWER: 'மனிதவளத்தை ஈடுபடுத்த',
    purpose_EXHIBITIONS_TRADE_FAIRS: 'கண்காட்சிகள், வணிக/வர்த்தக திருவிழாக்களில் பங்கேற்பு',
    purpose_EXPERT_SPECIALIST_PROJECT: 'நடந்து கொண்டிருக்கும் திட்டத்துடன் தொடர்புடைய நிபுணர்/சிறப்பு வல்லுநர்',
    purpose_CONDUCTING_TOURS: 'சுற்றுப்பயணங்களை நடத்துதல்',
    purpose_GIAN_LECTURES: 'குளோபல் அகாடெமிக் நெட்வொர்க்ஸ் (GIAN) கீழ் விரிவுரைகள் நடத்த',
    purpose_SPORTS_RELATED_ACTIVITY: 'விளையாட்டு சார்ந்த செயல்பாடு',
    purpose_TO_JOIN_THE_VESSEL: 'கப்பலில் சேர',
    purposePopup_SET_UP_BUSINESS_VENTURE: 'உங்களுக்கு தேவை: வணிக அழைப்பிதழின் நகல், வணிக அட்டையின் நகல்.',
    purposePopup_SALE_PURCHASE_TRADE: 'உங்களுக்கு தேவை: வணிக அழைப்பிதழின் நகல், வணிக அட்டையின் நகல்.',
    purposePopup_ATTEND_BUSINESS_MEETINGS: 'உங்களுக்கு தேவை: வணிக அழைப்பிதழின் நகல், வணிக அட்டையின் நகல்.',
    purposePopup_TO_RECRUIT_MANPOWER: 'உங்களுக்கு தேவை: வணிக அழைப்பிதழின் நகல், வணிக அட்டையின் நகல்.',
    purposePopup_EXHIBITIONS_TRADE_FAIRS: 'உங்களுக்கு தேவை: வணிக அழைப்பிதழின் நகல், வணிக அட்டையின் நகல்.',
    purposePopup_EXPERT_SPECIALIST_PROJECT: 'உங்களுக்கு தேவை: வணிக அழைப்பிதழின் நகல், வணிக அட்டையின் நகல்.',
    purposePopup_CONDUCTING_TOURS: 'உங்களுக்கு தேவை: வணிக அழைப்பிதழின் நகல், வணிக அட்டையின் நகல்.',
    purposePopup_GIAN_LECTURES: 'உங்களுக்கு தேவை: வெளிநாட்டு ஆசிரியருக்கு ஹோஸ்ட் நிறுவனத்தின் அழைப்பு, IIT காரக்பூர் வழங்கிய GIAN கீழ் சான்றளிப்பு உத்தரவின் நகல், ஆசிரியர் எடுக்கும் பாடங்களின் சினாப்ஸிஸ் நகல்.',
    purposePopup_SPORTS_RELATED_ACTIVITY: 'உங்களுக்கு தேவை: இந்திய அரசின் இளைஞர் விவகாரங்கள் மற்றும் விளையாட்டு அமைச்சகம் (விளையாட்டு துறை) அங்கீகாரம், இந்தியாவில் உள்ள தொடர்புடைய விளையாட்டு கூட்டமைப்பு/சங்கத்தின் வெளிநாட்டு விளையாட்டு அணிகள் மற்றும் விளையாட்டு வீரர்களுக்கான அழைப்பு மடல்.',
    purposePopup_TO_JOIN_THE_VESSEL: 'உங்களுக்கு தேவை: இந்தியாவில் அமைந்துள்ள இந்திய/வெளிநாட்டு கப்பல் முகவரிடமிருந்து ஸ்பான்சர் செய்யும் கடிதம், கடலோடிகளின் தொடர்ச்சியான வெளியேற்ற சான்றிதழ் (CDC) நகல்.',
    ebusinessDocsTitle: 'ஆதரவு ஆவணங்களை பதிவேற்றவும்',
    ebusinessDocsDesc: 'தேர்ந்தெடுத்த பயண நோக்கத்திற்கு தேவையான PDF களை பதிவேற்றவும்.',
    uploadPdf: 'PDF பதிவேற்றவும்',
    docUploaded: 'பதிவேற்றப்பட்டது',
    replace: 'மாற்று',
    ebusinessUploadError: 'ஆவணம் பதிவேற்ற தோல்வி. மீண்டும் முயற்சிக்கவும்.',
    doc_BUSINESS_INVITATION: 'வணிக அழைப்பு (நகல்)',
    doc_BUSINESS_CARD: 'வணிக அட்டை',
    doc_GIAN_INVITATION: 'வெளிநாட்டு ஆசிரியருக்கு ஹோஸ்ட் நிறுவனத்தின் அழைப்பு',
    doc_GIAN_SANCTION_ORDER: 'GIAN கீழ் சான்றளிப்பு உத்தரவு நகல் (IIT காரக்பூர்)',
    doc_GIAN_SYNOPSIS: 'ஆசிரியர் எடுக்கும் பாடங்களின் சினாப்ஸிஸ்',
    doc_SPORTS_APPROVAL: 'இந்திய அரசு அங்கீகாரம் (இளைஞர் விவகாரங்கள் மற்றும் விளையாட்டு அமைச்சகம்)',
    doc_SPORTS_INVITATION_LETTER: 'இந்தியாவில் விளையாட்டு கூட்டமைப்பு/சங்கத்தின் அழைப்பு மடல்',
    doc_VESSEL_SPONSORING_LETTER: 'இந்தியாவில் கப்பல் முகவரிடமிருந்து ஸ்பான்சர் கடிதம்',
    doc_VESSEL_CDC: 'கடலோடிகளின் தொடர்ச்சியான வெளியேற்ற சான்றிதழ் (CDC) நகல்',

    // Step 2: Upload Documents
    passportMandatory: 'கடவுச்சீட்டு (கட்டாயம்) *',
    photographMandatory: 'கடவுச்சீட்டு அளவு புகைப்படம் (கட்டாயம்) *',
    passportUploaded: 'கடவுச்சீட்டு பதிவேற்றப்பட்டது',
    photographUploaded: 'புகைப்படம் பதிவேற்றப்பட்டது',
    takePhoto: 'புகைப்படம் எடுக்கவும்',
    uploadFile: 'அல்லது கோப்பை பதிவேற்றவும்',
    remove: 'அகற்று',
    uploadPhotograph: 'புகைப்படத்தை பதிவேற்றவும்',
    clickToSelectFile: 'கோப்பைத் தேர்ந்தெடுக்க கிளிக் செய்யவும்',
    passportValidityNote: '• கடவுச்சீட்டு குறைந்தது 6 மாதங்களுக்கு செல்லுபடியாகும்',
    passportClearNote: '• தெளிவான படம், அனைத்து மூலைகளும் தெரியும்',
    photoFrontFacing: '• முன்பக்கம், எளிய பின்னணி',
    photoRecentNote: '• தெளிவான, சமீபத்திய புகைப்படம்',
    photoFormatJpeg: '• வடிவம்: JPEG',
    photoSizeRequirements: '• அளவு: குறைந்தபட்சம் 10 KB, அதிகபட்சம் 1 MB',
    photoDimensionRequirements: '• குறைந்தபட்ச பரிமாணங்கள் 350 பிக்சல்கள் (அகலம்) × 350 பிக்சல்கள் (உயரம்).',
    photoWhiteBackgroundNote: '• வெள்ளை பின்னணியுடன் சமீபத்திய முன் நோக்கிய புகைப்படம்.',

    // Step 3: Travel Details
    portOfArrivalLabel: 'வந்தடையும் விமான நிலையம் *',
    portOfArrivalPlaceholder: 'வந்தடையும் விமான நிலையத்தை தேர்ந்தெடுக்கவும்',
    expectedArrivalLabel: 'எதிர்பார்க்கப்படும் வரவு தேதி *',
    placesToVisitLabel: 'பார்வையிட எண்ணிய இடங்கள் *',
    placesToVisitPlaceholder: 'உ.தா., தில்லி, ஆக்ரா, ஜெய்ப்பூர், மும்பை',
    placesToVisitHelper: 'நீங்கள் பார்வையிட திட்டமிடும் நகரங்கள் அல்லது இடங்களை பட்டியலிடுங்கள்',

    // Step 4: Personal & Family Details
    fatherDetails: 'தந்தையின் விவரங்கள்',
    fatherNameLabel: 'தந்தையின் முழு பெயர் *',
    fatherNamePlaceholder: 'தந்தையின் முழு பெயரை உள்ளிடவும்',
    fatherBirthplaceLabel: 'தந்தையின் பிறந்த இடம் *',
    fatherBirthplacePlaceholder: 'நகரம், நாடு',
    motherDetails: 'தாயின் விவரங்கள்',
    motherNameLabel: 'தாயின் முழு பெயர் *',
    motherNamePlaceholder: 'தாயின் முழு பெயரை உள்ளிடவும்',
    motherBirthplaceLabel: 'தாயின் பிறந்த இடம் *',
    motherBirthplacePlaceholder: 'நகரம், நாடு',
    maritalStatusLabel: 'திருமண நிலை *',
    maritalStatusPlaceholder: 'திருமண நிலையை தேர்ந்தெடுக்கவும்',
    single: 'திருமணமாகாதவர்',
    married: 'திருமணமானவர்',
    other: 'மற்றவை',
    spouseDetails: 'வாழ்க்கைத் துணையின் விவரங்கள்',
    spouseNameLabel: 'வாழ்க்கைத் துணையின் முழு பெயர் *',
    spouseNamePlaceholder: 'வாழ்க்கைத் துணையின் முழு பெயரை உள்ளிடவும்',
    spouseNationalityLabel: 'வாழ்க்கைத் துணையின் தேசியம் *',
    spouseNationalityPlaceholder: 'வாழ்க்கைத் துணையின் தேசியத்தை உள்ளிடவும்',
    spouseBirthplaceLabel: 'வாழ்க்கைத் துணையின் பிறந்த இடம் *',
    spouseBirthplacePlaceholder: 'நகரம், நாடு',

    // Step 5: Address, Education & Religion
    residentialAddressLabel: 'விண்ணப்பதாரரின் குடியிருப்பு முகவரி *',
    residentialAddressPlaceholder: 'உங்கள் முழுமையான குடியிருப்பு முகவரியை உள்ளிடவும்',
    educationLabel: 'கல்வித் தகுதிகள் *',
    educationPlaceholder: 'உ.தா., பட்டமேற்படிப்பு',
    religionLabel: 'மதம் *',
    religionPlaceholder: 'உங்கள் மதத்தை உள்ளிடவும்',
    educationalQualificationInstituteLabel: 'எந்த கல்லூரி/பல்கலைக்கழகத்தில் இருந்து பெற்ற தகுதி?',
    educationalQualificationInstitutePlaceholder: 'உ.தா. கொழும்பு பல்கலைக்கழகம்',

    // Step 6: Travel History
    visitedIndiaLabel: 'நீங்கள் முன்பு இந்தியா சென்றிருக்கிறீர்களா? *',
    citiesVisitedInIndiaLabel: 'இந்தியாவில் பார்வையிட்ட நகரங்கள்',
    citiesVisitedInIndiaPlaceholder: 'நீங்கள் இந்தியாவில் பார்வையிட்ட நகரங்களை பட்டியலிடுங்கள்',
    previousVisaIdLabel: 'உங்கள் முந்தைய விசா அடையாள எண்ணைக் குறிப்பிடுங்கள்',
    previousVisaIdPlaceholder: 'விசா அடையாள எண்',
    visitedSAARCLabel: 'கடந்த 3 ஆண்டுகளில் SAARC நாடுகளை பார்வையிட்டீர்களா? *',
    saarcCountriesList: '(ஆப்கானிஸ்தான், வங்காளதேசம், பூட்டான், இந்தியா, மாலத்தீவுகள், நேபாளம், பாகிஸ்தான்)',
    overstayHistoryLabel: 'விசா காலாவதியான வரலாறு உள்ளதா? *',
    visaRejectionsLabel: 'முந்தைய விசா நிராகரிப்புகள் உள்ளதா? *',
    refusedEntryLabel: 'எந்த நாட்டிற்கும் நுழைவு மறுக்கப்பட்டதா? *',
    arrestedConvictedLabel: 'நீங்கள் எப்போதாவது எந்த நாட்டின் நீதிமன்றத்தால் கைது / வழக்கு / தண்டனை பெற்றிருக்கிறீர்களா? *',
    traffickingCrimesLabel: 'மனித கடத்தல் / போதைப்பொருள் கடத்தல் / குழந்தை துஷ்பிரயோகம் / பெண்கள் மீதான குற்றம் / பொருளாதார குற்றம் / நிதி மோசடியில் ஈடுபட்டிருக்கிறீர்களா? *',
    cyberTerrorViolenceLabel: 'சைபர் குற்றம் / பயங்கரவாத நடவடிக்கைகள் / சேதம் / உளவு / இன அழிப்பு / அரசியல் கொலை / அல்லது பிற வன்முறைச் செயல்களில் ஈடுபட்டிருக்கிறீர்களா? *',
    glorifyTerrorismLabel: 'பயங்கரவாத வன்முறையை நியாயப்படுத்தும் அல்லது புகழும் கருத்துக்களை, அல்லது பிறரை பயங்கரவாத அல்லது தீவிர குற்றச் செயல்களுக்கு ஊக்குவிக்கும் கருத்துக்களை எந்த வகையிலும் வெளிப்படுத்தியிருக்கிறீர்களா? *',
    soughtAsylumLabel: 'எந்த நாட்டிலும் அரசியல் அல்லது பிற அடைக்கலம் கோரியிருக்கிறீர்களா? *',
    travelExplanationLabel: 'தயவுசெய்து விளக்கம் அளிக்கவும்',
    travelExplanationPlaceholder: 'சூழ்நிலைகள் பற்றிய விவரங்களை அளிக்கவும்',
    overstayExplanationLabel: 'தயவுசெய்து காலாவதியானதற்கான விளக்கம் அளிக்கவும்',
    overstayExplanationPlaceholder: 'காலாவதியான சூழ்நிலைகள் பற்றிய விவரங்களை அளிக்கவும்',
    visaRejectionExplanationLabel: 'தயவுசெய்து விசா நிராகரிப்புக்கான விளக்கம் அளிக்கவும்',
    visaRejectionExplanationPlaceholder: 'விசா நிராகரிப்பு சூழ்நிலைகள் பற்றிய விவரங்களை அளிக்கவும்',
    refusedEntryExplanationLabel: 'தயவுசெய்து நுழைவு மறுப்புக்கான விளக்கம் அளிக்கவும்',
    refusedEntryExplanationPlaceholder: 'நுழைவு மறுப்பு சூழ்நிலைகள் பற்றிய விவரங்களை அளிக்கவும்',

    // Step 7: Reference in India
    referenceNotRequiredTourism: 'சுற்றுலாவிற்கு இந்தியாவில் குறிப்பு தேவையில்லை.',
    proceedNextStep: 'நீங்கள் அடுத்த படிக்கு செல்லலாம்.',
    referenceNameLabel: 'இந்தியாவில் உறவினர் / நண்பரின் பெயர் *',
    referenceNamePlaceholder: 'குறிப்பு பெயரை உள்ளிடவும்',
    referenceAddressLabel: 'முகவரி *',
    referenceAddressPlaceholder: 'இந்தியாவில் முழுமையான முகவரியை உள்ளிடவும்',
    referencePhoneLabel: 'தொலைபேசி எண் *',
    referencePhonePlaceholder: '+91 XXXXXXXXXX',

    // Step 8: Review & Declaration
    applicationSummary: 'விண்ணப்ப சுருக்கம்',
    personalInformation: 'தனிப்பட்ட தகவல்',
    documentsUploaded: 'ஆவணங்கள் பதிவேற்றப்பட்டன',
    travelDetails: 'பயண விவரங்கள்',
    personalFamilyDetails: 'தனிப்பட்ட & குடும்ப விவரங்கள்',
    additionalInformation: 'கூடுதல் தகவல்',
    arrival: 'வரவு',
    port: 'துறைமுகம்',
    father: 'தந்தை',
    mother: 'தாய்',
    maritalStatusValue: 'நிலை',
    educationValue: 'கல்வி',
    religionValue: 'மதம்',
    declarationText: 'இதன்மூலம், மேலே வழங்கப்பட்ட தகவல்கள் என் அறிவுக்கும் நம்பிக்கைக்கும் எட்டியவரை உண்மையும் சரியானதுமாகும் என்று நான் அறிவிக்கிறேன். மேலே குறிப்பிடப்பட்ட தகவல்களில் ஏதேனும் பொய்யானது, தவறானது, வழிதவறச் செய்வது அல்லது தவறாக பிரதிநிதித்துவப்படுத்துவது என கண்டறியப்பட்டால், அதற்கான பொறுப்பை நான் ஏற்க வேண்டியிருக்கலாம் என்பதை நான் அறிவேன்.',
    acceptDeclarationPrompt: 'உங்கள் விண்ணப்பத்தை சமர்ப்பிக்க அறிவிப்பை ஏற்றுக்கொள்ளவும்.',

    // Navigation & Actions
    stepOf: 'படி {step} / {total}',
    submitButton: 'விண்ணப்பத்தை சமர்ப்பிக்கவும்',
    processing: 'செயலாக்குகிறது...',

    // Common
    loading: 'ஏற்றுகிறது...',
    error: 'ஏதோ தவறு ஏற்பட்டுள்ளது',
    tryAgain: 'மீண்டும் முயற்சிக்கவும்',
    cancel: 'ரத்து செய்யவும்',
    confirm: 'உறுதிப்படுத்தவும்',
    yes: 'ஆம்',
    no: 'இல்லை',
    close: 'மூடவும்',

    // Language
    language: 'மொழி',
    english: 'English',
    sinhala: 'සිංහල',
    tamil: 'தமிழ்',
    selectLanguageDesc: 'தயவுசெய்து விண்ணப்பத்திற்கான உங்கள் விருப்பமான மொழியைத் தேர்ந்தெடுக்கவும்',
  }

};

export function getTranslation(lang: Language) {
  return translations[lang];
}
